import { getSupabase, getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey, createSupabaseClient, getSupabaseConfig, supabase as workingSupabase } from '../config/supabase'
import { createServiceClient } from '../config/service-client'
import { createClient } from '@supabase/supabase-js' // Temporarily kept for remaining instances
import { detectUserTimezone, convertTimeToTimezone, formatTimeInTimezone } from '../utils/timezone-utils'

console.error('🌍🌍🌍 TIMEZONE SUPPORT 2025-10-28: auth-service.ts with automatic timezone detection! 🌍🌍🌍')

// 🎯 PERFORMANCE FIX: Cache client to prevent excessive recreation and console spam
let cachedSupabaseClient: any = null

// Helper function to get a safe Supabase client with caching
const getSafeSupabaseClient = () => {
  // Return cached client if available
  if (cachedSupabaseClient) {
    return cachedSupabaseClient
  }
  
  console.log('🔧 Initializing Supabase client (one-time setup)...')
  
  try {
    let client = getSupabase()
    
    if (!client) {
      console.log('⚠️ Primary client unavailable, using fallback creation...')
      try {
        client = createSupabaseClient()
      } catch (createError) {
        console.error('❌ Fallback client creation failed:', createError)
        throw new Error('Supabase client not available')
      }
    }
    
    if (!client) {
      throw new Error('Supabase client not available')
    }
    
    // Validate client has core methods
    if (!client.from || !client.auth || !client.rpc) {
      console.error('❌ Client missing core methods')
      throw new Error('Supabase client missing core methods')
    }
    
    // Cache the working client
    cachedSupabaseClient = client
    console.log('✅ Supabase client initialized and cached successfully')
    
    return client
  } catch (error) {
    console.error('❌ Error getting Supabase client:', error)
    throw error
  }
}

// 🎯 PERFORMANCE FIX: Lazy initialization to prevent immediate client creation
// This prevents calling getSafeSupabaseClient() on module load
const getSupabaseClientProxy = () => {
  const client = getSafeSupabaseClient()
  return client
}

// Use lazy proxy to eliminate excessive function calls
const supabase = {
  get auth() { return getSupabaseClientProxy().auth },
  from: (table: string) => getSupabaseClientProxy().from(table),
  rpc: (fn: string, args?: any) => getSupabaseClientProxy().rpc(fn, args),
}

export type UserRole = 'patient' | 'provider' | 'admin'

interface SignupData {
  email: string
  password: string
  firstName: string
  lastName: string
  role: UserRole
  specialty?: string
  licenseNumber?: string
  phone?: string
  licensed?: string[]
  timezone?: string
}

interface LoginData {
  email: string
  password: string
}

class AuthService {
  // Helper function to convert treatment types and specialties to display names
  private getTreatmentDisplayName(treatmentType?: string, specialty?: string): string {
    // First try to map treatment type
    const treatmentMap: { [key: string]: string } = {
      'weight_loss': 'Weight Loss',
      'mens_health': 'HRT & Men\'s Health',
      'womens_health': 'Women\'s Health',
      'peptides': 'Peptide Therapy',
      'general': 'General Medicine'
    }

    if (treatmentType && treatmentMap[treatmentType]) {
      return treatmentMap[treatmentType]
    }

    // Fallback to specialty mapping
    const specialtyMap: { [key: string]: string } = {
      'endocrinology': 'Weight Loss',
      'men\'s health': 'HRT & Men\'s Health', 
      'mens health': 'HRT & Men\'s Health',
      'women\'s health': 'Women\'s Health',
      'womens health': 'Women\'s Health',
      'peptide therapy': 'Peptide Therapy',
      'peptides': 'Peptide Therapy',
      'general practice': 'General Medicine',
      'semaglutide visit': 'Weight Loss',
      'testosterone visit': 'HRT & Men\'s Health',
      'semaglutide': 'Weight Loss',
      'testosterone': 'HRT & Men\'s Health'
    }

    const normalizedSpecialty = specialty?.toLowerCase() || ''
    return specialtyMap[normalizedSpecialty] || 'Provider'
  }

  async signUp(data: SignupData) {
    try {
      console.log('🔄 Starting signup process for:', data.email, 'role:', data.role)
      
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            role: data.role,
            specialty: data.specialty,
            license_number: data.licenseNumber,
            phone: data.phone,
            licensed: data.licensed
          }
        }
      })

      if (authError) {
        console.error('❌ Auth signup error:', authError)
        throw authError
      }
      
      console.log('✅ Auth user created successfully:', authData.user?.id)
      console.log('🚀 PROFILE CREATION FIX: Starting profile and role record creation...')

      if (!authData.user) {
        throw new Error('No user data returned from signup')
      }

      // Use timezone from form data, fallback to auto-detection
      const userTimezone = data.timezone || detectUserTimezone()
      console.log('🌍 TIMEZONE SELECTION: Using timezone:', userTimezone, data.timezone ? '(User selected)' : '(Auto-detected)')

      // Create or update profile record (upsert to handle existing profiles)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          role: data.role,
          timezone: userTimezone, // Add timezone support
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        })

      if (profileError) {
        console.error('❌ Profile creation/update error:', profileError)
        throw profileError
      }

      console.log('✅ Profile created/updated successfully')

      // Create role-specific records
      if (data.role === 'provider') {
        // Create or update provider record (upsert to handle existing providers)
        const { data: providerData, error: providerError } = await supabase
          .from('providers')
          .upsert({
            profile_id: authData.user.id,
            specialty: data.specialty || 'General Practice',
            license_number: data.licenseNumber || '',
            licensed: data.licensed || [],
            phone: data.phone || '',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'profile_id'
          })
          .select()
          .single()

        if (providerError) {
          console.error('❌ Provider creation/update error:', providerError)
          throw providerError
        }

        console.log('✅ Provider record created/updated:', providerData.id)

        // Create default schedule for provider (Monday-Friday, 9 AM - 5 PM)
        // Skip if schedules already exist to avoid constraint violations
        const { data: existingSchedules } = await supabase
          .from('provider_schedules')
          .select('id')
          .eq('provider_id', providerData.id)
          .limit(1)

        if (!existingSchedules || existingSchedules.length === 0) {
          console.log('🔄 Creating default provider schedules...')
          const schedulePromises = []
          for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
            schedulePromises.push(
              supabase
                .from('provider_schedules')
                .insert({
                  provider_id: providerData.id,
                  day_of_week: dayOfWeek,
                  start_time: '09:00:00',
                  end_time: '17:00:00',
                  treatment_types: ['weight_loss', 'diabetes_management'],
                  is_available: true,
                  timezone: userTimezone, // Add timezone to schedules
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                })
            )
          }

          const scheduleResults = await Promise.all(schedulePromises)
          const scheduleErrors = scheduleResults.filter(result => result.error)
          
          if (scheduleErrors.length > 0) {
            console.error('❌ Schedule creation errors:', scheduleErrors)
            console.warn('⚠️ Provider registration succeeded but schedule creation failed')
            // Don't throw error - provider registration is still successful
          } else {
            console.log('✅ Provider schedules created successfully')
          }
        } else {
          console.log('✅ Provider schedules already exist, skipping creation')
        }
      } else if (data.role === 'patient') {
        // Create or update patient record (upsert to handle existing patients)
        const { error: patientError } = await supabase
          .from('patients')
          .upsert({
            profile_id: authData.user.id,
            email: data.email,           // 🆕 ADD email for simplified Stripe queries
            first_name: data.firstName,  // 🆕 ADD first_name for simplified Stripe queries  
            last_name: data.lastName,    // 🆕 ADD last_name for simplified Stripe queries
            phone: data.phone || '',
            has_completed_intake: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'profile_id'
          })

        if (patientError) {
          console.error('❌ Patient creation/update error:', patientError)
          throw patientError
        }

        console.log('✅ Patient record created/updated successfully')
      } else if (data.role === 'admin') {
        // Create or update admin record using service client to bypass RLS
        console.log('🔧 Creating admin record with RLS bypass...')
        
        // Use service client with RLS bypass for admin creation
        // Use singleton service client instead of creating new one
        const serviceClient = createServiceClient()

        const { error: adminError } = await serviceClient
          .from('admins')
          .upsert({
            profile_id: authData.user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'profile_id'
          })

        if (adminError) {
          console.error('❌ Admin creation/update error:', adminError)
          throw adminError
        }

        console.log('✅ Admin record created/updated successfully')
      }

      console.log('✅ All profile and role records created successfully')
      return { data: authData, error: null }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  async signIn(data: LoginData) {
    try {
      console.log('🔐 LOGIN DEBUG: Attempting login with email:', data.email)
      console.log('🔐 LOGIN DEBUG: Password length:', data.password?.length || 0)
      console.log('🔐 LOGIN DEBUG: Using Supabase client:', supabase ? 'exists' : 'missing')
      
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      })

      console.log('🔐 LOGIN DEBUG: Auth response - data exists:', !!authData)
      console.log('🔐 LOGIN DEBUG: Auth error:', authError)

      if (authError) {
        console.error('❌ LOGIN ERROR:', authError.message, 'Status:', authError.status)
        throw authError
      }

      console.log('✅ LOGIN SUCCESS: User authenticated')
      return { data: authData, error: null }
    } catch (error: any) {
      console.error('❌ LOGIN CATCH ERROR:', error.message, 'Status:', error.status)
      return { data: null, error }
    }
  }

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { error: null }
    } catch (error: any) {
      return { error }
    }
  }

  async getCurrentUser() {
    try {
      console.log('🔍 getCurrentUser: Calling supabase.auth.getUser()...')
      
      // Navigation-friendly timeout to match auth-provider (10 seconds for consistency)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('getCurrentUser timeout after 10 seconds')), 10000)
      })
      
      const authPromise = supabase.auth.getUser()
      const { data: { user }, error } = await Promise.race([authPromise, timeoutPromise])
      
      console.log('🔍 getCurrentUser: supabase.auth.getUser() completed')
      console.log('🔍 getCurrentUser: error?', !!error, error?.message)
      console.log('🔍 getCurrentUser: user?', !!user, user?.id)
      if (error) throw error
      return { user, error: null }
    } catch (error: any) {
      console.error('❌ getCurrentUser: Error caught:', error)
      return { user: null, error }
    }
  }

  async getCurrentSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) throw error
      return { session, error: null }
    } catch (error: any) {
      return { session: null, error }
    }
  }

  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }

  // Get user role from auth metadata
  async getUserRole(): Promise<UserRole | null> {
    try {
      console.log('🔍 getUserRole: Starting getCurrentUser call...')
      const { user } = await this.getCurrentUser()
      console.log('🔍 getUserRole: getCurrentUser completed, user exists?', !!user)
      console.log('🔍 getUserRole: user_metadata exists?', !!user?.user_metadata)
      console.log('🔍 getUserRole: role value:', user?.user_metadata?.role)
      const role = user?.user_metadata?.role || null
      console.log('🔍 getUserRole: returning role:', role)
      return role
    } catch (error) {
      console.error('❌ getUserRole: Error occurred:', error)
      return null
    }
  }

  // Get user profile data based on role (includes profile info + role-specific info)
  async getUserProfile() {
    try {
      const { user } = await this.getCurrentUser()
      if (!user) return { data: null, error: new Error('No user found') }

      const role = user.user_metadata?.role
      if (!role) return { data: null, error: new Error('No role found') }

      console.log('🔍 getUserProfile: Loading profile for role:', role, 'user:', user.id)

      // Get profile data first
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (profileError) {
        console.error('❌ getUserProfile: Profile error:', profileError)
        return { data: null, error: profileError }
      }

      console.log('✅ getUserProfile: Profile data loaded:', profileData)

      // Get role-specific data
      let tableName = ''
      switch (role) {
        case 'patient':
          tableName = 'patients'
          break
        case 'provider':
          tableName = 'providers'
          break
        case 'admin':
          tableName = 'admins'
          break
        default:
          return { data: null, error: new Error('Invalid role') }
      }

      let roleData = null
      let roleError = null

      // For admin users, use service client to bypass RLS (same pattern as signup)
      if (role === 'admin') {
        console.log('🔧 getUserProfile: Using service client for admin role with RLS bypass')
        
        const baseUrl = getEnvironmentAwareSupabaseUrl()
        const serviceKey = getEnvironmentAwareSupabaseServiceKey()
        
        if (!serviceKey) {
          console.error('❌ getUserProfile: No service key available for admin query')
          return { data: null, error: new Error('Service key not configured for admin access') }
        }
        
        const serviceClient = createClient(baseUrl, serviceKey, {
          auth: {
            autoRefreshToken: false,
            persistSession: false
          }
        })
        
        const { data: adminData, error: adminError } = await serviceClient
          .from('admins')
          .select('*')
          .eq('profile_id', user.id)
          .single()
        
        roleData = adminData
        roleError = adminError
        
        console.log('🔍 getUserProfile: Admin query result:', { roleData, roleError })
      } else {
        // For patient/provider, use regular authenticated client
        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .eq('profile_id', user.id)
          .single()
        
        roleData = data
        roleError = error
        
        console.log('🔍 getUserProfile:', tableName, 'query result:', { roleData, roleError })
      }

      if (roleError) {
        console.error('❌ getUserProfile: Role data error:', roleError)
        return { data: null, error: roleError }
      }

      // Combine profile and role data
      const combinedData = {
        ...roleData,
        ...profileData,
        // Keep role-specific id as the primary id, profile id as profile_id
        profile_id: profileData.id,
        id: roleData.id
      }

      console.log('✅ getUserProfile: Combined data:', combinedData)
      return { data: combinedData, error: null }
    } catch (error: any) {
      console.error('❌ getUserProfile: Exception:', error)
      return { data: null, error }
    }
  }

  // Alias for getUserProfile - used by ProviderDashboard
  async getRoleData() {
    return this.getUserProfile()
  }

  // Patient-specific methods
  async getPatientMedicationPreferences(userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      console.log('🚀🚀🚀 RLS BYPASS FIX getPatientMedicationPreferences 2025-10-30-11:30')
      console.log('🔍 getPatientMedicationPreferences for user profile ID:', targetUserId)

      // Use service client with RLS bypass for querying patient data
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      const serviceClient = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${serviceKey}`,
            'apikey': serviceKey,
            'Role': 'service_role'
          }
        }
      })

      // First get the patient table ID from the profile ID
      const { data: patientData, error: patientError } = await serviceClient
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: [], error: null }
      }

      console.log('🔍 Found patient table ID:', patientData.id)

      // Get medication preferences with medication information using service client
      console.log('🔍 Querying patient_medication_preferences for patient_id:', patientData.id)
      
      const { data, error } = await serviceClient
        .from('patient_medication_preferences')
        .select(`
          *,
          medications (
            id,
            name,
            description,
            category,
            active,
            monthly_price_id,
            quarterly_price_id,
            price_per_month,
            quarterly_discount_percent
          )
        `)
        .eq('patient_id', patientData.id)
        .order('created_at', { ascending: false })
      
      console.log('🔍 Query completed. Data:', data, 'Error:', error)

      if (error) {
        console.log('❌ Error fetching medication preferences:', error)
        return { data: null, error }
      }

      console.log('🔍 Raw medication preferences data:', data)

      // Enrich each preference with dosage information
      const enrichedData = await Promise.all(
        (data || []).map(async (preference) => {
          let medicationWithDosages = preference.medications
          let selectedDosage = null // The specific dosage the patient has selected

          if (preference.medications?.id) {
            // Get all dosage information for this medication
            const { data: dosages } = await serviceClient
              .from('medication_dosages')
              .select('*')
              .eq('medication_id', preference.medications.id)
              .eq('available', true)
              .order('sort_order', { ascending: true })

            medicationWithDosages = {
              ...preference.medications,
              medication_dosages: dosages || []
            }

            // Get the SPECIFIC selected dosage based on medication_dosage_id
            // This is the key fix: use the foreign key to get the exact dosage with correct Stripe price
            if (preference.medication_dosage_id) {
              console.log('🔍 Fetching selected dosage info for medication_dosage_id:', preference.medication_dosage_id)
              const { data: selectedDosageData } = await serviceClient
                .from('medication_dosages')
                .select('*')
                .eq('id', preference.medication_dosage_id)
                .single()

              if (selectedDosageData) {
                selectedDosage = selectedDosageData
                console.log('✅ Found selected dosage with stripe_price_id:', selectedDosageData.stripe_price_id)
              }
            } else if (preference.preferred_dosage && dosages && dosages.length > 0) {
              // Fallback: Try to match by preferred_dosage text field
              console.log('⚠️ No medication_dosage_id set, trying to match by preferred_dosage:', preference.preferred_dosage)
              selectedDosage = dosages.find((d: any) =>
                d.strength === preference.preferred_dosage ||
                d.strength.toLowerCase().includes(preference.preferred_dosage.toLowerCase()) ||
                preference.preferred_dosage.toLowerCase().includes(d.strength.toLowerCase())
              )
              if (selectedDosage) {
                console.log('✅ Found dosage match by text:', selectedDosage.stripe_price_id)
              }
            }
          }

          // Debug: Log raw database fields before transformation
          console.log('🔍 RAW DB FIELDS DEBUG - ID:', preference.id)
          console.log('🔍 RAW DB FIELDS DEBUG - refill_requested:', preference.refill_requested)
          console.log('🔍 RAW DB FIELDS DEBUG - refills:', preference.refills)
          console.log('🔍 RAW DB FIELDS DEBUG - status:', preference.status)
          console.log('🔍 RAW DB FIELDS DEBUG - next_prescription_due:', preference.next_prescription_due)
          console.log('🔍 RAW DB FIELDS DEBUG - all_fields:', Object.keys(preference))

          const transformedData = {
            ...preference,
            medication_name: preference.medications?.name || 'Unknown Medication',
            medications: medicationWithDosages,
            provider_notes: preference.notes,
            // Add the selected dosage info directly for easy access in checkout
            medication_dosages: selectedDosage, // This will be the specific selected dosage with stripe_price_id
            // Transform snake_case database fields to camelCase for UI compatibility
            refillRequested: preference.refill_requested,
            nextPrescriptionDue: preference.next_prescription_due,
            approvalDate: preference.approval_date,
            refillRequestedDate: preference.refill_requested_date,
            visitRequested: preference.visit_requested,
            visitRequestedDate: preference.visit_requested_date,
            refills: preference.refills // Add the missing refills field
          }

          // Debug: Log the final transformed data
          console.log('🔍 FINAL TRANSFORMED DATA - refillRequested:', transformedData.refillRequested)
          console.log('🔍 FINAL TRANSFORMED DATA - refills:', transformedData.refills)
          console.log('🔍 FINAL TRANSFORMED DATA - status:', transformedData.status)

          return transformedData
        })
      )

      console.log('✅ Enriched medication preferences with selected dosage information:', enrichedData.length)
      return { data: enrichedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getPatientMedicationPreferences:', error)
      return { data: null, error }
    }
  }

  async getPatientMedicationOrders(patientId: string) {
    try {
      console.log('🔍 getPatientMedicationOrders for patient ID:', patientId)

      // Get medication orders without complex joins to avoid 400 errors
      const { data, error } = await supabase
        .from('medication_orders')
        .select(`
          id,
          patient_id,
          medication_id,
          quantity,
          unit_price,
          total_amount,
          payment_status,
          fulfillment_status,
          payment_method,
          payment_date,
          shipped_date,
          estimated_delivery,
          tracking_number,
          admin_notes,
          created_at,
          updated_at
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })

      if (error) {
        console.log('❌ Error fetching medication orders:', error)
        return { data: [], error }
      }

      console.log('🔍 Raw medication orders data:', data)

      // If no orders, return empty array
      if (!data || data.length === 0) {
        return { data: [], error: null }
      }
      
      // Get unique medication IDs to fetch medication details
      const medicationIds = [...new Set(data.map(order => order.medication_id))].filter(Boolean)
      console.log('🔍 Fetching medication details for IDs:', medicationIds)
      
      // Fetch medication details using service role client to bypass RLS
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      let medicationsMap = new Map()
      
      if (medicationIds.length > 0) {
        const medicationsUrl = `${baseUrl}/rest/v1/medications?id=in.(${medicationIds.join(',')})&select=id,name,description,dosage_form,unit_price`
        console.log('🔍 Fetching medications from:', medicationsUrl)
        
        const medicationsResponse = await fetch(medicationsUrl, {
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${serviceKey}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (medicationsResponse.ok) {
          const medicationsData = await medicationsResponse.json()
          console.log('✅ Fetched medication details:', medicationsData)
          medicationsData.forEach((med: any) => {
            medicationsMap.set(med.id, med)
          })
        } else {
          console.log('❌ Failed to fetch medication details:', medicationsResponse.status)
        }
      }

      // Transform the data with medication names
      const transformedData = data?.map((order: any) => {
        const medication = medicationsMap.get(order.medication_id)
        return {
          id: order.id,
          patient_id: order.patient_id,
          medication_id: order.medication_id,
          medication_name: medication?.name || 'Unknown Medication',
          dosage: medication?.dosage_form || 'Not specified',
          quantity: order.quantity,
          unit_price: order.unit_price,
          total_amount: order.total_amount,
          payment_status: order.payment_status,
          fulfillment_status: order.fulfillment_status,
          payment_method: order.payment_method,
          payment_date: order.payment_date,
          shipped_date: order.shipped_date,
          estimated_delivery: order.estimated_delivery,
          tracking_number: order.tracking_number,
          admin_notes: order.admin_notes,
          created_at: order.created_at,
          updated_at: order.updated_at
        }
      }) || []

      console.log('🔍 Transformed medication orders with names:', transformedData)

      return { data: transformedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getPatientMedicationOrders:', error)
      return { data: [], error }
    }
  }

  async updateMedicationOrderAdmin(orderId: string, updateData: {
    payment_status?: string
    payment_method?: string
    payment_date?: string
    fulfillment_status?: string
    tracking_number?: string
    shipped_date?: string
    estimated_delivery?: string
    admin_notes?: string
  }) {
    try {
      console.log('🔄 Updating medication order (admin):', orderId, updateData)

      // Clean up the update data to convert empty strings to null for timestamp fields
      const cleanedUpdateData = {
        ...updateData,
        payment_date: updateData.payment_date === '' ? null : updateData.payment_date,
        shipped_date: updateData.shipped_date === '' ? null : updateData.shipped_date,
        estimated_delivery: updateData.estimated_delivery === '' ? null : updateData.estimated_delivery,
      }

      // Use environment-aware service client for admin operations
      const { createClient } = await import('@supabase/supabase-js')
      const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js')
      
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      console.log('🚀 DEPLOYMENT VERIFIED 2025-11-27: updateMedicationOrderAdmin using CORRECTED service key for production database')
      console.log('🔧 updateMedicationOrderAdmin: Using environment-aware service client for admin operations')
      console.log('🔧 Database URL:', baseUrl)
      console.log('🔧 Service key length:', serviceKey?.length || 0)
      
      if (!serviceKey) {
        throw new Error('Service key not available for admin operations')
      }
      
      const supabase = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false
        }
      })
      
      // Debug: Test service role access first by checking if the order exists
      console.log('🔍 Testing service role access by querying order:', orderId)
      const { data: existingOrder, error: queryError } = await supabase
        .from('medication_orders')
        .select('*')
        .eq('id', orderId)
        .single()
      
      if (queryError) {
        console.error('❌ Failed to query existing order (service role issue?):', queryError)
        // Try direct REST API call as alternative
        console.log('🔧 Attempting direct REST API call...')
        try {
          const response = await fetch(`${baseUrl}/rest/v1/medication_orders?id=eq.${orderId}&select=*`, {
            method: 'GET',
            headers: {
              'apikey': serviceKey,
              'Authorization': `Bearer ${serviceKey}`,
              'Content-Type': 'application/json'
            }
          })
          
          console.log('🔧 REST API response status:', response.status)
          if (response.ok) {
            const restData = await response.json()
            console.log('✅ REST API query successful:', restData?.length, 'orders found')
          } else {
            const errorText = await response.text()
            console.error('❌ REST API query failed:', response.status, errorText)
          }
        } catch (restError) {
          console.error('❌ REST API query exception:', restError)
        }
        return { success: false, error: queryError }
      }
      
      console.log('✅ Found existing order:', existingOrder?.id)
      
      // Proceed with update
      const { data, error } = await supabase
        .from('medication_orders')
        .update(cleanedUpdateData)
        .eq('id', orderId)
        .select()

      if (error) {
        console.error('❌ Error updating medication order:', error)
        console.error('❌ Update data that failed:', cleanedUpdateData)
        return { success: false, error }
      }

      if (!data || data.length === 0) {
        console.error('❌ No medication order found with ID:', orderId, 'or insufficient permissions')
        return { success: false, error: {
          message: 'Medication order not found or insufficient permissions to update',
          code: 'NOT_FOUND_OR_FORBIDDEN'
        }}
      }

      console.log('✅ Medication order updated successfully:', data[0])
      return { success: true, data: data[0], error: null }
    } catch (error: any) {
      console.error('❌ Exception updating medication order:', error)
      return { success: false, error }
    }
  }

  async getOrdersByPreferenceId(preferenceId: string) {
    try {
      console.log('🔍 getOrdersByPreferenceId for preference ID:', preferenceId)

      // CORRECT: First get approval_id from medication_approvals where preference_id matches
      // Then query medication_orders where approval_id matches
      const { data: approvals, error: approvalError } = await supabase
        .from('medication_approvals')
        .select('id')
        .eq('preference_id', preferenceId)
        .eq('status', 'approved')

      if (approvalError) {
        console.log('❌ Error fetching approvals for preference ID:', approvalError)
        return { data: [], error: approvalError }
      }

      if (!approvals || approvals.length === 0) {
        console.log('🔍 No approved medication approvals found for preference ID:', preferenceId)
        return { data: [], error: null }
      }

      // Get all approval IDs for this preference
      const approvalIds = approvals.map(approval => approval.id)
      console.log('🔍 Found approval IDs for preference:', approvalIds)

      // Now query medication_orders using the approval_ids
      const { data, error } = await supabase
        .from('medication_orders')
        .select(`
          id,
          patient_id,
          medication_id,
          approval_id,
          quantity,
          unit_price,
          total_amount,
          payment_status,
          fulfillment_status,
          payment_method,
          payment_date,
          shipped_date,
          estimated_delivery,
          tracking_number,
          admin_notes,
          created_at,
          updated_at
        `)
        .in('approval_id', approvalIds)
        .order('created_at', { ascending: false })

      if (error) {
        console.log('❌ Error fetching orders by preference ID:', error)
        return { data: [], error }
      }

      console.log('🔍 Raw orders by preference data:', data)

      // For now, if there are no orders for this preference, return empty array
      // This is expected for most preferences since medication orders are created
      // when medications are approved and processed
      if (!data || data.length === 0) {
        console.log('🔍 No orders found for preference ID:', preferenceId)
        return { data: [], error: null }
      }

      // Transform the data to match expected format
      const transformedData = data?.map((order: any) => ({
        id: order.id,
        patient_id: order.patient_id,
        medication_id: order.medication_id,
        medication_name: 'Unknown', // We'll get this separately if needed
        dosage: 'Not specified', // We'll get this from the preference if needed
        quantity: order.quantity,
        unit_price: order.unit_price,
        total_amount: order.total_amount,
        payment_status: order.payment_status,
        fulfillment_status: order.fulfillment_status,
        payment_method: order.payment_method,
        payment_date: order.payment_date,
        shipped_date: order.shipped_date,
        estimated_delivery: order.estimated_delivery,
        tracking_number: order.tracking_number,
        admin_notes: order.admin_notes,
        created_at: order.created_at,
        updated_at: order.updated_at
      })) || []

      console.log('🔍 Transformed orders by preference:', transformedData)

      return { data: transformedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getOrdersByPreferenceId:', error)
      return { data: [], error }
    }
  }

  async getPatientMedicationsDetailed(patientId: string) {
    try {
      console.log('🔍 getPatientMedicationsDetailed for patient ID:', patientId)

      // Get medication orders without complex joins to avoid 400 errors
      // We'll return empty array for now since medication orders may not exist yet
      const { data, error } = await supabase
        .from('medication_orders')
        .select(`
          id,
          quantity,
          unit_price,
          total_amount,
          payment_status,
          payment_date,
          shipped_date,
          tracking_number,
          created_at
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })

      if (error) {
        console.log('❌ Error fetching detailed medications:', error)
        return []
      }

      console.log('🔍 Raw detailed medication data:', data)

      // Transform the data to match expected format
      const transformedData = data?.map((order: any) => ({
        id: order.id,
        name: 'Unknown Medication', // We'll get this from preferences if needed
        dosage: 'Not specified', // We'll get this from preferences if needed
        supply: `${order.quantity || 30} day supply`,
        status: order.payment_status || 'pending',
        lastPaymentDate: order.last_payment_date,
        sentToPharmacyDate: order.sent_to_pharmacy_date,
        shippedToPharmacyDate: order.shipped_date,
        trackingNumber: order.tracking_number
      })) || []

      console.log('🔍 Transformed detailed medication data:', transformedData)

      return transformedData
    } catch (error: any) {
      console.log('❌ Exception in getPatientMedicationsDetailed:', error)
      return []
    }
  }

  async getAvailableMedications() {
    try {
      console.log('🔍 getAvailableMedications: Using direct fetch API to bypass RLS issues...')
      
      // Use direct fetch API to avoid Supabase client RLS conflicts
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      const apiUrl = `${baseUrl}/rest/v1/medications`
      const headers = {
        'apikey': serviceKey,
        'Authorization': `Bearer ${serviceKey}`,
        'Content-Type': 'application/json'
      }
      
      const queryParams = new URLSearchParams({
        'active': 'eq.true',
        'select': '*,medication_dosages(*)',
        'order': 'sort_order.asc,name.asc'
      })
      
      console.log('🔍 getAvailableMedications: Executing direct fetch API call')
      console.log('🔍 API URL:', `${apiUrl}?${queryParams.toString()}`)
      
      const response = await fetch(`${apiUrl}?${queryParams.toString()}`, {
        method: 'GET',
        headers: headers
      })
      
      console.log('🔍 getAvailableMedications: Fetch response status:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ getAvailableMedications: Fetch error:', errorText)
        return { data: null, error: `HTTP ${response.status}: ${errorText}` }
      }
      
      const rawMedications = await response.json()
      console.log('✅ getAvailableMedications: Fetch success. Raw data count:', rawMedications?.length)
      console.log('✅ getAvailableMedications: Raw data sample:', rawMedications?.slice(0, 2))

      // Filter medications that have available dosages
      const medicationsWithDosages = rawMedications?.filter((med: any) => 
        med.medication_dosages && med.medication_dosages.length > 0
      ) || []

      console.log('✅ Loaded medications with dosages:', medicationsWithDosages.length, 'medications')
      if (medicationsWithDosages.length > 0) {
        console.log('📝 Sample medication with dosages:', medicationsWithDosages[0])
      }
      
      return { data: medicationsWithDosages, error: null }
    } catch (error: any) {
      console.error('❌ Exception loading medications:', error)
      return { data: null, error }
    }
  }

  async getMedicationDosages(medicationId: string) {
    try {
      const { data, error } = await supabase
        .from('medication_dosages')
        .select('*')
        .eq('medication_id', medicationId)
        .eq('available', true)
        .order('sort_order')

      return { data, error }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  async getPatientVisits(userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      const { data, error } = await supabase
        .from('appointments')
        .select('*, providers(*)')
        .eq('patient_id', targetUserId)
        .order('appointment_date', { ascending: false })

      return { data, error }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  async getPatientAppointments(userId?: string, upcomingOnly?: boolean) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      console.log('🚀🚀🚀 DIRECT SUPABASE FIX getPatientAppointments 2025-11-12-12:20')
      console.log('🔍 getPatientAppointments for user profile ID:', targetUserId, 'upcomingOnly:', upcomingOnly)
      
      // Use environment-aware direct Supabase calls (same pattern as getPatientMedicationPreferences)
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      const serviceClient = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${serviceKey}`,
            'apikey': serviceKey,
            'Role': 'service_role'
          }
        }
      })

      // First get the patient table ID from the profile ID using service client
      const { data: patientData, error: patientError } = await serviceClient
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: [], error: null }
      }

      console.log('🔍 Found patient table ID:', patientData.id)
      console.log('🔍 🔍 EXPECTED PATIENT ID from provider visits: b134d47f-54dc-438e-9fb0-fde94210f3c2')
      console.log('🔍 🔍 ACTUAL PATIENT ID from profile lookup:', patientData.id)

      // Get appointments using service client to bypass RLS with proper relationships
      let query = serviceClient
        .from('appointments')
        .select(`
          *,
          providers:provider_id (
            id,
            specialty,
            profile_id,
            profiles!inner(timezone)
          )
        `)
        .eq('patient_id', patientData.id)

      // Apply filtering if upcomingOnly is true
      if (upcomingOnly) {
        const today = new Date().toISOString().split('T')[0]
        query = query
          .gte('appointment_date', today)
          .in('status', ['scheduled', 'confirmed'])
          .or('Async.is.null,Async.eq.false')
          .order('appointment_date', { ascending: true })
      } else {
        query = query.order('appointment_date', { ascending: false })
      }

      const { data, error } = await query

      if (error) {
        console.log('❌ Error fetching appointments:', error)
        return { data: null, error }
      }

      console.log('🔍 Raw appointment data (environment-aware Supabase):', data)

      // For each appointment, fetch provider profile info using the provider's profile_id
      const enrichedAppointments = await Promise.all(
        (data || []).map(async (appointment) => {
          try {
            let providerName = `Dr. Provider ${appointment.provider_id?.slice(0,8)}`
            let providerSpecialty = appointment.providers?.specialty || 'General Practice'
            
            // Get provider profile information if we have the profile_id
            if (appointment.providers?.profile_id) {
              const { data: profileData } = await serviceClient
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', appointment.providers.profile_id)
                .single()
              
              if (profileData?.first_name && profileData?.last_name) {
                providerName = `Dr. ${profileData.first_name} ${profileData.last_name}`
              }
            }

            return {
              ...appointment,
              provider_name: providerName,
              provider_specialty: providerSpecialty
            }
          } catch (err) {
            console.log('⚠️ Could not fetch provider info for appointment:', appointment.id, err)
            return {
              ...appointment,
              provider_name: `Dr. Provider ${appointment.provider_id?.slice(0,8)}`,
              provider_specialty: 'General Practice'
            }
          }
        })
      )

      console.log('✅ Environment-aware appointment data with provider names:', enrichedAppointments)
      return { data: enrichedAppointments, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getPatientAppointments:', error)
      return { data: null, error }
    }
  }

  // Get assigned provider's patient communication email
  async getAssignedProviderEmail(userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      console.log('🔍 getAssignedProviderEmail for user profile ID:', targetUserId)

      // Use environment-aware service client to bypass RLS
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()

      const serviceClient = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${serviceKey}`,
            'apikey': serviceKey,
            'Role': 'service_role'
          }
        }
      })

      // Get the patient record with provider_email
      const { data: patientData, error: patientError } = await serviceClient
        .from('patients')
        .select('id, provider_email')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: null, error: patientError || new Error('Patient not found') }
      }

      console.log('🔍 Found patient with provider_email:', patientData.provider_email)

      // If no provider email, return null
      if (!patientData.provider_email) {
        console.log('ℹ️ No provider email set for this patient')
        return { data: null, error: null }
      }

      console.log('✅ Found provider email:', patientData.provider_email)

      // Return provider email directly from patients table
      const result = {
        email: patientData.provider_email,
        providerId: null, // Could be added later if needed
        providerName: 'Provider' // Could be added later if needed
      }

      return { data: result, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getAssignedProviderEmail:', error)
      return { data: null, error }
    }
  }

  async createMedicationPreference(preferences: { medicationId: string; dosage: string; userId?: string }) {
    try {
      console.log('🚀 DEPLOYMENT: createMedicationPreference with dosage lookup for Stripe pricing!')
      const targetUserId = preferences.userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { success: false, error: new Error('No user ID') }

      // First, get the patient table ID from the profile ID
      const client = getSafeSupabaseClient()
      const { data: patient, error: patientError } = await client
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patient) {
        return { success: false, error: new Error('Patient record not found') }
      }

      // Look up medication_dosage_id based on dosage string and medication_id
      // This ensures the correct Stripe price ID is associated with the preference from the start
      let medicationDosageId = null
      console.log('🔍 Create: Looking up medication_dosage_id for medication:', preferences.medicationId, 'dosage:', preferences.dosage)

      const { data: dosageRecord, error: dosageError } = await client
        .from('medication_dosages')
        .select('id, stripe_price_id, unit_price')
        .eq('medication_id', preferences.medicationId)
        .eq('strength', preferences.dosage)
        .single()

      if (dosageRecord && !dosageError) {
        console.log('✅ Create: Found medication_dosage_id:', dosageRecord.id, 'with stripe_price_id:', dosageRecord.stripe_price_id)
        medicationDosageId = dosageRecord.id
      } else {
        // Try a more flexible match if exact match fails
        const { data: dosageRecords } = await client
          .from('medication_dosages')
          .select('id, strength, stripe_price_id, unit_price')
          .eq('medication_id', preferences.medicationId)

        const matchingDosage = dosageRecords?.find(d =>
          d.strength === preferences.dosage ||
          d.strength.toLowerCase().includes(preferences.dosage.toLowerCase()) ||
          preferences.dosage.toLowerCase().includes(d.strength.toLowerCase())
        )

        if (matchingDosage) {
          console.log('✅ Create: Found matching dosage via partial match:', matchingDosage.id)
          medicationDosageId = matchingDosage.id
        } else {
          console.log('⚠️ Create: No matching dosage found for:', preferences.dosage, 'available:', dosageRecords?.map(d => d.strength))
        }
      }

      const insertData: any = {
        patient_id: patient.id, // Use the patient table ID, not the profile ID
        medication_id: preferences.medicationId, // Now always use the real medication ID
        preferred_dosage: preferences.dosage,
        status: 'pending'
      }

      // Add medication_dosage_id if found
      if (medicationDosageId) {
        insertData.medication_dosage_id = medicationDosageId
      }

      console.log('🔍 Inserting medication preference with dosage link:', insertData)

      const { data, error } = await client
        .from('patient_medication_preferences')
        .insert(insertData)
        .select()
        .single()

      if (error) {
        return { success: false, error }
      }

      console.log('✅ Medication preference created with dosage link:', data)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.log('❌ Exception in createMedicationPreference:', error)
      return { success: false, error }
    }
  }

  async updateMedicationPreference(preferenceId: string, preferences: {
    medicationId?: string;
    dosage?: string;
    status?: string;
    frequency?: string;
    providerNotes?: string;
    faxed?: boolean;
    supplyDays?: number;
    otherDosage?: string;
  }, providerId?: string) {
    try {
      const callId = Math.random().toString(36).substr(2, 9)
      console.log('🔄 Updating medication preference (provider):', preferenceId, preferences, 'CALL_ID:', callId)
      console.log('🔍 OTHER_DOSAGE DEBUG: Raw otherDosage value received:', preferences.otherDosage)
      console.log('🔍 OTHER_DOSAGE DEBUG: otherDosage type:', typeof preferences.otherDosage)

      // Build update object with only provided fields
      const updateData: any = {
        updated_at: new Date().toISOString()
      }

      if (preferences.medicationId !== undefined) {
        updateData.medication_id = preferences.medicationId
      }
      if (preferences.dosage !== undefined) {
        updateData.preferred_dosage = preferences.dosage
      }
      if (preferences.status !== undefined) {
        updateData.status = preferences.status
      }
      if (preferences.frequency !== undefined) {
        updateData.frequency = preferences.frequency
      }
      if (preferences.providerNotes !== undefined) {
        updateData.notes = preferences.providerNotes
      }
      if (preferences.faxed !== undefined) {
        updateData.faxed = preferences.faxed ? new Date().toISOString() : null
      }
      if (preferences.supplyDays !== undefined) {
        updateData.supply_days = preferences.supplyDays
      }
      if (preferences.otherDosage !== undefined) {
        updateData.other_dosage = preferences.otherDosage
        console.log('🔍 OTHER_DOSAGE DEBUG: Adding other_dosage to updateData:', preferences.otherDosage)
      } else {
        console.log('🔍 OTHER_DOSAGE DEBUG: otherDosage is undefined, not updating')
      }

      // Look up medication_dosage_id based on dosage string and medication_id
      // This ensures the correct Stripe price ID is associated with the preference
      if (preferences.dosage !== undefined) {
        // Get the current preference to find the medication_id if not provided
        let medicationId = preferences.medicationId
        if (!medicationId) {
          const { data: currentPref } = await supabase
            .from('patient_medication_preferences')
            .select('medication_id')
            .eq('id', preferenceId)
            .single()
          medicationId = currentPref?.medication_id
        }

        if (medicationId) {
          console.log('🔍 Provider update: Looking up medication_dosage_id for medication:', medicationId, 'dosage:', preferences.dosage)

          // Find the medication_dosage record that matches this dosage strength
          const { data: dosageRecord, error: dosageError } = await supabase
            .from('medication_dosages')
            .select('id, stripe_price_id, unit_price')
            .eq('medication_id', medicationId)
            .eq('strength', preferences.dosage)
            .single()

          if (dosageRecord && !dosageError) {
            console.log('✅ Provider update: Found medication_dosage_id:', dosageRecord.id, 'with stripe_price_id:', dosageRecord.stripe_price_id)
            updateData.medication_dosage_id = dosageRecord.id
          } else {
            // Try a more flexible match if exact match fails
            const { data: dosageRecords } = await supabase
              .from('medication_dosages')
              .select('id, strength, stripe_price_id, unit_price')
              .eq('medication_id', medicationId)

            const matchingDosage = dosageRecords?.find(d =>
              d.strength === preferences.dosage ||
              d.strength.toLowerCase().includes(preferences.dosage!.toLowerCase()) ||
              preferences.dosage!.toLowerCase().includes(d.strength.toLowerCase())
            )

            if (matchingDosage) {
              console.log('✅ Provider update: Found matching dosage via partial match:', matchingDosage.id)
              updateData.medication_dosage_id = matchingDosage.id
            } else {
              console.log('❌ Provider update: No matching dosage found for:', preferences.dosage)
            }
          }
        }
      }

      console.log('🔍 Update data:', updateData, 'CALL_ID:', callId)
      console.log('🔍 OTHER_DOSAGE DEBUG: updateData.other_dosage specifically:', updateData.other_dosage)
      console.log('🔍 OTHER_DOSAGE DEBUG: Full updateData object keys:', Object.keys(updateData))
      console.log('🔍 OTHER_DOSAGE DEBUG: Does updateData contain other_dosage?', 'other_dosage' in updateData)
      console.log('🔍 DEBUG: updateMedicationPreference called from stack:', new Error().stack?.split('\n')[2], 'CALL_ID:', callId)
      
      const { data, error } = await supabase
        .from('patient_medication_preferences')
        .update(updateData)
        .eq('id', preferenceId)
        .select('id, patient_id, medication_id, preferred_dosage, frequency, notes, status, requested_date, created_at, updated_at, medication_dosage_id, faxed, next_prescription_due, supply_days, refill_requested, refills, subscription_type, stripe_subscription_id, auto_refill, provider_id, other_dosage')
        .single()

      if (error) {
        console.log('❌ Error updating medication preference:', error, 'CALL_ID:', callId)
        return { success: false, error }
      }
      
      console.log('✅ Medication preference updated successfully:', data, 'CALL_ID:', callId)
      console.log('🔍 OTHER_DOSAGE DEBUG: Response data.other_dosage:', data?.other_dosage)
      console.log('🔍 OTHER_DOSAGE DEBUG: Full response data keys:', Object.keys(data || {}))
      console.log('🔍 OTHER_DOSAGE DEBUG: Raw database response data:', JSON.stringify(data, null, 2))
      
      // Note: Orders are now only created via fax workflow, not on approval
      // The fax trigger (create_order_on_fax_trigger) handles order creation
      
      return { success: true, data, error: null }
    } catch (error: any) {
      console.log('❌ Exception updating medication preference:', error)
      return { success: false, error }
    }
  }

  async updatePatientMedicationPreference(preferenceId: string, preferences: { medicationId?: string; dosage?: string }) {
    const callId = Math.random().toString(36).substr(2, 9)
    try {
      console.log('🔄 Updating medication preference with dosage lookup:', preferenceId, preferences, 'PATIENT_CALL_ID:', callId)

      // Build update object
      const updateData: any = {
        preferred_dosage: preferences.dosage,
        status: 'pending', // Reset status to pending when updated
        refill_requested: false, // Reset refill request when dosage is changed
        updated_at: new Date().toISOString()
      }

      if (preferences.medicationId) {
        updateData.medication_id = preferences.medicationId
      }

      // Look up medication_dosage_id based on dosage string and medication_id
      // This ensures the correct Stripe price ID is associated with the preference
      if (preferences.dosage) {
        // First get the current preference to find the medication_id if not provided
        let medicationId = preferences.medicationId
        if (!medicationId) {
          const { data: currentPref } = await supabase
            .from('patient_medication_preferences')
            .select('medication_id')
            .eq('id', preferenceId)
            .single()
          medicationId = currentPref?.medication_id
        }

        if (medicationId) {
          console.log('🔍 Looking up medication_dosage_id for medication:', medicationId, 'dosage:', preferences.dosage)

          // Find the medication_dosage record that matches this dosage strength
          const { data: dosageRecord, error: dosageError } = await supabase
            .from('medication_dosages')
            .select('id, stripe_price_id, unit_price')
            .eq('medication_id', medicationId)
            .eq('strength', preferences.dosage)
            .single()

          if (dosageRecord && !dosageError) {
            console.log('✅ Found medication_dosage_id:', dosageRecord.id, 'with stripe_price_id:', dosageRecord.stripe_price_id)
            updateData.medication_dosage_id = dosageRecord.id
          } else {
            console.log('⚠️ Could not find exact dosage match, trying partial match for:', preferences.dosage)
            // Try a more flexible match if exact match fails
            const { data: dosageRecords } = await supabase
              .from('medication_dosages')
              .select('id, strength, stripe_price_id, unit_price')
              .eq('medication_id', medicationId)

            // Find a dosage that contains or matches the preference dosage
            const matchingDosage = dosageRecords?.find(d =>
              d.strength === preferences.dosage ||
              d.strength.toLowerCase().includes(preferences.dosage.toLowerCase()) ||
              preferences.dosage.toLowerCase().includes(d.strength.toLowerCase())
            )

            if (matchingDosage) {
              console.log('✅ Found matching dosage via partial match:', matchingDosage.id)
              updateData.medication_dosage_id = matchingDosage.id
            } else {
              console.log('❌ No matching dosage found for:', preferences.dosage, 'available:', dosageRecords?.map(d => d.strength))
            }
          }
        }
      }

      console.log('🔍 Final update data:', updateData, 'PATIENT_CALL_ID:', callId)
      console.log('🔍 DEBUG: updatePatientMedicationPreference called from stack:', new Error().stack?.split('\n')[2], 'PATIENT_CALL_ID:', callId)

      const { data, error } = await supabase
        .from('patient_medication_preferences')
        .update(updateData)
        .eq('id', preferenceId)
        .select('id, patient_id, medication_id, preferred_dosage, frequency, notes, status, requested_date, created_at, updated_at, medication_dosage_id, faxed, next_prescription_due, supply_days, refill_requested, refills, subscription_type, stripe_subscription_id, auto_refill, provider_id, other_dosage')
        .single()

      if (error) {
        console.log('❌ Error updating medication preference:', error, 'PATIENT_CALL_ID:', callId)
        return { success: false, error }
      }

      console.log('✅ Medication preference updated with dosage link:', data, 'PATIENT_CALL_ID:', callId)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.log('❌ Exception in updatePatientMedicationPreference:', error, 'PATIENT_CALL_ID:', callId)
      return { success: false, error }
    }
  }

  async requestPrescriptionRefill(preferenceId: string, patientProfileId: string) {
    try {
      console.log('🔄 Requesting prescription refill:', preferenceId, patientProfileId)
      
      const { data, error } = await supabase
        .rpc('request_prescription_refill', {
          p_preference_id: preferenceId,
          p_patient_profile_id: patientProfileId
        })

      if (error) {
        console.error('❌ Error requesting refill:', error)
        return { success: false, error }
      }
      
      console.log('✅ Refill request result:', data)
      
      if (data.success) {
        return { success: true, data, error: null }
      } else {
        return { success: false, error: { message: data.error } }
      }
    } catch (error: any) {
      console.error('❌ Exception requesting refill:', error)
      return { success: false, error }
    }
  }

  async getPatientAssignedProviders(userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      console.log('🚀🚀🚀 PATIENT ASSIGNMENT FIX 2025-10-29-23:30: Added provider query fallback for 406 errors! 🚀🚀🚀')
      console.log('🔍 getPatientAssignedProviders for user:', targetUserId)
      
      // First test basic table access to check RLS/auth
      console.log('🔍 Testing basic patient_assignments table access...')
      const { data: testData, error: testError } = await supabase
        .from('patient_assignments')
        .select('id')
        .limit(1)
      
      if (testError) {
        console.log('❌ Basic table access failed - RLS/Auth issue:', testError)
        console.log('❌ Error code:', testError.code)
        console.log('❌ Error message:', testError.message)
        console.log('❌ Error details:', testError.details)
        return { data: null, error: testError }
      }
      console.log('✅ Basic table access successful:', testData)

      // First get the patient ID from the profile ID
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: [], error: null }
      }

      console.log('🔍 Found patient ID:', patientData.id)

      // Get providers assigned to this patient - fetch assignments first, then providers
      // Fixed: Avoid problematic join syntax, do two separate queries
      const { data: assignments, error } = await supabase
        .from('patient_assignments')
        .select('*')
        .eq('patient_id', patientData.id)
        .eq('active', true)
      
      if (error) {
        console.log('❌ Error fetching patient assignments:', error)
        return { data: null, error }
      }

      console.log('🔍 Raw assignment data:', assignments)
      
      if (!assignments || assignments.length === 0) {
        console.log('⚠️ No assignments found for patient')
        return { data: [], error: null }
      }

      // Get provider details for each assignment with fallback
      const providerPromises = assignments.map(async (assignment: any) => {
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('id, profile_id, specialty')
          .eq('id', assignment.provider_id)
          .single()
        
        if (providerError) {
          console.log('❌ Error fetching provider details:', assignment.provider_id, providerError)
          console.log('❌ Provider error code:', providerError.code)
          console.log('❌ Provider error message:', providerError.message)
          
          // Fallback: Create provider info from assignment and known data
          console.log('🔄 Using fallback provider data for:', assignment.provider_id)
          return {
            ...assignment,
            providers: {
              id: assignment.provider_id,
              profile_id: assignment.provider_id, // Use provider_id as fallback
              specialty: 'General Practice',
              license_number: 'N/A',
              phone: 'N/A'
            }
          }
        }
        
        return {
          ...assignment,
          providers: provider
        }
      })

      const data = await Promise.all(providerPromises)
      const validData = data.filter(item => item !== null)

      console.log('🔍 Provider assignment data with details:', validData)

      // Transform the data to include provider details  
      const transformedData = validData.map(assignment => {
        const treatmentDisplayName = this.getTreatmentDisplayName(assignment.treatment_type, assignment.providers.specialty)
        
        return {
          id: assignment.providers.id, // This is the provider table ID we need
          profile_id: assignment.providers.profile_id,
          specialty: assignment.providers.specialty,
          license_number: assignment.providers.license_number,
          phone: assignment.providers.phone,
          // Use treatment-based display names
          first_name: `Dr. ${treatmentDisplayName}`,
          last_name: 'Provider',
          email: `provider${assignment.providers.id.slice(0,8)}@joinomu.com`,
          name: `${treatmentDisplayName}`,
          treatment_type: assignment.treatment_type,
          is_primary: assignment.is_primary,
          assigned_date: assignment.assigned_date
        }
      }) || []

      console.log('🔍 Transformed provider data:', transformedData)

      return { data: transformedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getPatientAssignedProviders:', error)
      return { data: null, error }
    }
  }

  async getProviderByProfileId(profileId: string) {
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('profile_id', profileId)
        .single()

      return { data, error }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  // Provider-specific methods
  private async buildPatientDataFromRecords(assignment: any, patientData: any, baseUrl: string, serviceKey: string) {
    // STEP 3B: Get profile information for the patient
    console.log(`🔍 PROVIDER DEBUG: Fetching profile for patient profile_id: ${patientData.profile_id}`)
    
    let profileData = null
    try {
      const profileResponse = await fetch(`${baseUrl}/rest/v1/profiles?id=eq.${patientData.profile_id}&select=first_name,last_name,email`, {
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (profileResponse.ok) {
        const profiles = await profileResponse.json()
        profileData = profiles[0]
        console.log(`✅ PROVIDER DEBUG: Found profile data:`, profileData)
      } else {
        console.log(`⚠️ PROVIDER DEBUG: No profile found for ${patientData.profile_id}`)
      }
    } catch (profileError) {
      console.log(`❌ PROVIDER DEBUG: Profile fetch error:`, profileError)
    }
    
    return {
      // Assignment info
      assignment_id: assignment.id,
      provider_id: assignment.provider_id,
      patient_id: assignment.patient_id,
      treatment_type: assignment.treatment_type,
      is_primary: assignment.is_primary,
      assigned_date: assignment.assigned_date,
      created_at: assignment.created_at,
      
      // Patient table info
      id: patientData.id,
      profile_id: patientData.profile_id,
      date_of_birth: patientData.date_of_birth,
      phone: patientData.phone,
      has_completed_intake: patientData.has_completed_intake,
      
      // Profile info (with fallback if profile missing)
      first_name: profileData?.first_name || `Patient ${assignment.patient_id.slice(0,8)}`,
      last_name: profileData?.last_name || '',
      email: profileData?.email || `patient${assignment.patient_id.slice(0,8)}@example.com`,
      
      // UI compatibility
      medications: []
    }
  }

  async getAssignedPatients(providerProfileId: string) {
    try {
      console.log('🚀🚀🚀 FINAL RLS BYPASS 2025-10-30-16:00: Using service key to bypass RLS policies!')
      
      const supabase = getSafeSupabaseClient()
      
      // Step 1: Get provider ID from profile ID using authenticated client (no RLS issue here)
      console.log('🔍 AUTH CLIENT: Looking up provider table ID for profile:', providerProfileId)
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select('id')
        .eq('profile_id', providerProfileId)
        .single()
      
      if (providerError || !providerData) {
        console.log('❌ Provider not found for profile ID:', providerProfileId, providerError)
        return { data: [], error: null }
      }
      
      console.log('✅ Found provider table ID:', providerData.id)
      
      // Step 2: Get patient assignments for this provider using authenticated client (no RLS issue here)
      console.log('🔍 AUTH CLIENT: Fetching assignments for provider:', providerData.id)
      const { data: assignments, error: assignmentsError } = await supabase
        .from('patient_assignments')
        .select('*')
        .eq('provider_id', providerData.id)
      
      if (assignmentsError) {
        console.error('❌ Error fetching assignments:', assignmentsError)
        return { data: [], error: assignmentsError.message }
      }
      
      console.log('✅ Found assignments:', assignments?.length || 0)
      console.log('🔍 Assignment sample:', assignments?.[0])
      
      if (!assignments || assignments.length === 0) {
        console.log('⚠️ No assignments found for provider')
        return { data: [], error: null }
      }
      
      // Extract patient IDs from assignments
      const patientIds = assignments.map((assignment: any) => assignment.patient_id)
      console.log('🔍 Patient IDs from assignments:', patientIds)
      
      // Step 3: Fetch patients using direct API call with service key (same pattern as working patient portal)
      console.log('🔧 DIRECT API: Using direct fetch with service key (same as working patient portal)')
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      console.log('🔍 SERVICE KEY DEBUG: Retrieved service key:', serviceKey ? `${serviceKey.substring(0, 20)}...` : 'NULL')
      console.log('🔍 SERVICE KEY DEBUG: Service key length:', serviceKey?.length || 0)
      console.log('🔍 SERVICE KEY DEBUG: Current environment URL:', baseUrl)
      
      if (!serviceKey) {
        console.error('❌ No service key available from getEnvironmentAwareSupabaseServiceKey()')
        return { data: [], error: 'Service key not configured' }
      }
      
      console.log('🔍 DIRECT API: Fetching patients for IDs with service key:', patientIds)
      
      // Use the same working pattern as patient portal provider lookups
      // CRITICAL FIX: Explicitly select ALL eligibility fields instead of using * selector
      // SCHEMA FIX: Removed treatment_type (doesn't exist in patients table) and non-existent date/assignment fields
      const eligibilityFields = 'id,profile_id,first_name,last_name,email,phone,date_of_birth,has_completed_intake,created_at,gender,selected_state,height_feet,height_inches,weight,bmi,activity_level,weight_loss_goals,motivations,treatment_preferences,medication_preference,diagnosed_conditions,chronic_diseases,family_medical_history,family_health,allergies,medication_history,procedures,supplements,eating_disorders,mental_health,self_harm_screening,drinking,drugs,smoking,heart_rate,gastrointestinal,side_effects,side_effect_guidance,gastrointestinal_dosing,energy_dosing,muscle_loss_dosing,challenges,challenges_elaborate,program_adherence,program_consistency,additional_info'
      
      // Try eq syntax instead of in syntax for single ID
      const patientsUrl = patientIds.length === 1 
        ? `${baseUrl}/rest/v1/patients?id=eq.${patientIds[0]}&select=${eligibilityFields}`
        : `${baseUrl}/rest/v1/patients?id=in.(${patientIds.join(',')})&select=${eligibilityFields}`
      console.log('🔍 DIRECT API: Full request URL:', patientsUrl)
      const patientsResponse = await fetch(patientsUrl, {
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json'
        }
      })
      
      console.log('🔍 DIRECT API: Response status:', patientsResponse.status, patientsResponse.statusText)
      
      if (!patientsResponse.ok) {
        const errorText = await patientsResponse.text()
        console.error('❌ Error fetching patients with direct API call:', patientsResponse.status, errorText)
        return { data: [], error: `Failed to fetch patients: ${patientsResponse.status}` }
      }
      
      const patients = await patientsResponse.json()
      console.log('🎉 SUCCESS! Found patients with direct API:', patients?.length || 0)
      console.log('✅ Patient records:', patients)
      console.log('🔍 DEBUG: Individual patient IDs being searched:', patientIds)
      console.log('🔍 DEBUG: Expected patient ID from assignment:', patientIds[0])
      
      // ENHANCED DEBUG: Let's test if the issue is with the query format
      console.log('🔧 ENHANCED DEBUG: Testing different query approaches...')
      
      // Test 1: Try without any filters to see if we can get ANY patients
      const allPatientsUrl = `${baseUrl}/rest/v1/patients?select=id,profile_id,created_at&limit=10`
      console.log('🔍 DEBUG TEST 1: Fetching all patients:', allPatientsUrl)
      try {
        const allPatientsResponse = await fetch(allPatientsUrl, {
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${serviceKey}`,
            'Content-Type': 'application/json'
          }
        })
        const allPatients = await allPatientsResponse.json()
        console.log('🔍 DEBUG TEST 1 RESULT: All patients count:', allPatients?.length || 0)
        console.log('🔍 DEBUG TEST 1 RESULT: All patients data:', allPatients)
      } catch (testError) {
        console.log('❌ DEBUG TEST 1 ERROR:', testError)
      }
      
      // Test 2: Try direct ID lookup with different syntax
      const directLookupUrl = `${baseUrl}/rest/v1/patients?id=eq.3f4237ba-e94d-42d2-8d2b-049925aedcb0`
      console.log('🔍 DEBUG TEST 2: Direct patient lookup:', directLookupUrl)
      try {
        const directResponse = await fetch(directLookupUrl, {
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${serviceKey}`,
            'Content-Type': 'application/json'
          }
        })
        const directResult = await directResponse.json()
        console.log('🔍 DEBUG TEST 2 RESULT: Direct lookup result:', directResult)
      } catch (testError) {
        console.log('❌ DEBUG TEST 2 ERROR:', testError)
      }
      
      if (!patients || patients.length === 0) {
        console.log('⚠️ No patients found for IDs despite service key access:', patientIds)
        return { data: [], error: null }
      }
      
      // Step 4: For each patient, fetch their profile information using direct API (same pattern)
      const finalPatients = []
      
      for (const patient of patients) {
        console.log('🔍 Processing patient:', patient.id, 'Profile ID:', patient.profile_id)
        
        // Fetch profile data using direct API call (same as working patient portal pattern)
        console.log('🔍 DIRECT API: Fetching profile with service key for:', patient.profile_id)
        const profileResponse = await fetch(`${baseUrl}/rest/v1/profiles?id=eq.${patient.profile_id}&select=*`, {
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${serviceKey}`,
            'Content-Type': 'application/json'
          }
        })
        
        let profile = null
        if (profileResponse.ok) {
          const profiles = await profileResponse.json()
          profile = profiles[0]
          console.log('✅ Profile fetched successfully for patient:', patient.id)
        } else {
          const errorText = await profileResponse.text()
          console.warn('⚠️ Could not fetch profile for patient:', patient.id, profileResponse.status, errorText)
        }
        
        // Find assignment details for this patient
        const assignment = assignments.find((a: any) => a.patient_id === patient.id)
        
        // Fetch recent previous appointment for this patient
        console.log('🔍 APPOINTMENTS: Fetching previous appointment for patient:', patient.id)
        let previousAppointment = null
        try {
          const previousApptResponse = await fetch(
            `${baseUrl}/rest/v1/appointments?patient_id=eq.${patient.id}&appointment_date=lt.${new Date().toISOString().split('T')[0]}&select=appointment_date,start_time,appointment_type,treatment_type&order=appointment_date.desc,start_time.desc&limit=1`,
            {
              headers: {
                'apikey': serviceKey,
                'Authorization': `Bearer ${serviceKey}`,
                'Content-Type': 'application/json'
              }
            }
          )
          
          if (previousApptResponse.ok) {
            const previousAppts = await previousApptResponse.json()
            if (previousAppts && previousAppts.length > 0) {
              const appt = previousAppts[0]
              previousAppointment = {
                date: appt.appointment_date,
                type: appt.appointment_type || appt.treatment_type || 'Consultation'
              }
              console.log('✅ Found previous appointment for', patient.id, ':', previousAppointment)
            }
          }
        } catch (apptError) {
          console.warn('⚠️ Error fetching previous appointment for patient:', patient.id, apptError)
        }

        // Fetch next upcoming appointment for this patient
        console.log('🔍 APPOINTMENTS: Fetching next appointment for patient:', patient.id)
        let nextAppointment = null
        try {
          const nextApptResponse = await fetch(
            `${baseUrl}/rest/v1/appointments?patient_id=eq.${patient.id}&appointment_date=gte.${new Date().toISOString().split('T')[0]}&select=appointment_date,start_time,appointment_type,treatment_type&order=appointment_date.asc,start_time.asc&limit=1`,
            {
              headers: {
                'apikey': serviceKey,
                'Authorization': `Bearer ${serviceKey}`,
                'Content-Type': 'application/json'
              }
            }
          )
          
          if (nextApptResponse.ok) {
            const nextAppts = await nextApptResponse.json()
            if (nextAppts && nextAppts.length > 0) {
              const appt = nextAppts[0]
              nextAppointment = {
                date: appt.appointment_date,
                type: appt.appointment_type || appt.treatment_type || 'Consultation'
              }
              console.log('✅ Found next appointment for', patient.id, ':', nextAppointment)
            }
          }
        } catch (apptError) {
          console.warn('⚠️ Error fetching next appointment for patient:', patient.id, apptError)
        }
        
        const enrichedPatient = {
          // CRITICAL FIX: Include ALL patient fields for eligibility data
          ...patient, // Spread all patient fields from database (includes eligibility fields)
          
          // Override/add specific computed fields
          name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Unknown Patient',
          email: profile?.email || patient.email || '',
          phone: patient.phone || profile?.phone || '',
          date_of_birth: patient.date_of_birth || profile?.date_of_birth || '',
          careTeam: [], // This would need additional queries to populate
          treatments: [], // This would need additional queries to populate
          medications: [], // This would need additional queries to populate
          status: ['Active'], // Default status
          treatmentType: assignment?.treatment_type || 'consultation',
          assignedDate: assignment?.assigned_date || assignment?.created_at,
          isPrimary: assignment?.is_primary || false,
          hasCompletedIntake: patient.has_completed_intake || false,
          
          // Appointment data for Previous/Next Visit columns
          previous_appointment: previousAppointment,
          next_appointment: nextAppointment,
          
          // Additional properties for compatibility
          first_name: profile?.first_name,
          last_name: profile?.last_name,
          assigned_providers: [providerProfileId],
          treatment_types: [assignment?.treatment_type || 'consultation']
        }
        
        finalPatients.push(enrichedPatient)
        console.log('✅ Successfully processed patient:', enrichedPatient.name || enrichedPatient.id)
      }
      
      console.log('🎉 RLS BYPASS SUCCESS! Returning', finalPatients.length, 'assigned patients')
      return { data: finalPatients, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getAssignedPatients:', error)
      return { data: null, error }
    }
  }

  async bookAppointment(appointmentData: {
    patientProfileId: string
    providerId: string
    appointmentDate: string
    startTime: string
    treatmentType: string
    appointmentType: string
    patientNotes?: string
    patientTimezone?: string
  }) {
    try {
      console.log('🌍 TIMEZONE BOOKING 2025-10-28: BookAppointment called with:', {
        patientProfileId: appointmentData.patientProfileId,
        providerId: appointmentData.providerId,
        appointmentDate: appointmentData.appointmentDate,
        startTime: appointmentData.startTime,
        treatmentType: appointmentData.treatmentType,
        patientTimezone: appointmentData.patientTimezone
      })
      
      // Get timezone information for both patient and provider
      const patientTimezone = appointmentData.patientTimezone || detectUserTimezone()
      
      // Get provider's timezone via providers table
      const { data: providerData } = await supabase
        .from('providers')
        .select('profile_id, profiles!inner(timezone)')
        .eq('id', appointmentData.providerId)
        .single()
      
      const providerTimezone = providerData?.profiles?.timezone || 'America/New_York'
      
      console.log(`🌍 Timezone context - Patient: ${patientTimezone}, Provider: ${providerTimezone}`)
      
      // Use the RPC function to book the appointment
      const { data, error } = await supabase
        .rpc('book_appointment', {
          p_patient_profile_id: appointmentData.patientProfileId,
          p_provider_id: appointmentData.providerId,
          p_appointment_date: appointmentData.appointmentDate,
          p_start_time: appointmentData.startTime,
          p_treatment_type: appointmentData.treatmentType,
          p_appointment_type: appointmentData.appointmentType,
          p_booked_by: 'patient',
          p_patient_notes: appointmentData.patientNotes
        })

      if (error) {
        console.error('❌ RPC book_appointment failed:', error)
        return { success: false, error }
      }

      const result = data?.[0]
      if (result?.success) {
        // Update the created appointment with timezone information
        const appointmentId = result.appointment_id
        console.log(`🌍 Adding timezone context to appointment ${appointmentId}`)
        
        const { error: updateError } = await supabase
          .from('appointments')
          .update({
            provider_timezone: providerTimezone,
            patient_timezone: patientTimezone,
            scheduled_timezone: patientTimezone // The timezone the patient was using when booking
          })
          .eq('id', appointmentId)
        
        if (updateError) {
          console.error('⚠️ Warning: Failed to update timezone information:', updateError)
          // Don't fail the booking, just log the warning
        } else {
          console.log('✅ Successfully added timezone context to appointment')
        }
        
        // Return enhanced result with timezone information
        return { 
          success: true, 
          data: {
            ...result,
            timezone_info: {
              provider_timezone: providerTimezone,
              patient_timezone: patientTimezone,
              scheduled_timezone: patientTimezone
            }
          }, 
          appointmentId: result.appointment_id, 
          error: null 
        }
      } else {
        return { success: false, error: new Error(result?.message || 'Failed to book appointment') }
      }
    } catch (error: any) {
      console.error('❌ Exception in bookAppointment:', error)
      return { success: false, error }
    }
  }

  async getAvailableSlots(
    providerId: string, 
    startDate: string, 
    endDate: string, 
    treatmentType?: string,
    patientTimezone?: string
  ) {
    try {
      console.log('🌍 TIMEZONE SLOTS 2025-10-28: Getting available slots for provider:', providerId)
      console.log('📅 Date parameters:', { startDate, endDate, treatmentType, patientTimezone })
      
      // Get provider's timezone from profiles via providers table
      console.log(`🔍 Querying provider timezone for provider ID: ${providerId}`)
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select('profile_id, profiles!inner(timezone)')
        .eq('id', providerId)
        .single()
      
      if (providerError) {
        console.error(`❌ Provider timezone query failed:`, providerError)
        console.log(`🔄 Attempting alternative query via service key...`)
        
        // Try alternative query via direct API call with service key
        try {
          const baseUrl = getEnvironmentAwareSupabaseUrl()
          const serviceKey = getEnvironmentAwareSupabaseServiceKey()
          const response = await fetch(`${baseUrl}/rest/v1/providers?select=profile_id,profiles!inner(timezone)&id=eq.${providerId}`, {
            headers: {
              'apikey': serviceKey,
              'Authorization': `Bearer ${serviceKey}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (response.ok) {
            const data = await response.json()
            console.log(`✅ Alternative timezone query success:`, data)
            if (data && data[0]?.profiles?.timezone) {
              const providerTimezone = data[0].profiles.timezone
              console.log(`🌍 Provider timezone found via service key: ${providerTimezone}`)
            }
          } else {
            console.error(`❌ Alternative timezone query failed: ${response.status}`)
          }
        } catch (altError) {
          console.error(`❌ Alternative timezone query exception:`, altError)
        }
      }
      
      const providerTimezone = providerData?.profiles?.timezone || 'America/New_York'
      const displayTimezone = patientTimezone || detectUserTimezone()
      
      console.log(`🌍 Provider timezone: ${providerTimezone}, Display timezone: ${displayTimezone}`)
      
      // First try the RPC function (may fail due to RLS)
      const rpcParams = {
        p_provider_id: providerId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_treatment_type: treatmentType
      }
      console.log('🔧 RPC parameters:', rpcParams)
      
      const { data, error } = await supabase
        .rpc('get_available_slots_for_provider', rpcParams)

      if (error) {
        console.error('❌ Error getting slots:', error)
      } else {
        console.log(`✅ Found ${data?.length || 0} available slots from RPC`)
      }

      // If RPC worked, add timezone conversion to results
      if (data && data.length > 0) {
        console.log('✅ RPC function returned slots, adding timezone conversion')
        const convertedSlots = data.map(slot => ({
          ...slot,
          // Keep original times for booking
          provider_slot_time: slot.slot_start_time,
          provider_timezone: providerTimezone,
          // Add converted times for display
          display_start_time: convertTimeToTimezone(
            slot.slot_start_time, 
            providerTimezone, 
            displayTimezone,
            slot.slot_date
          ),
          display_end_time: convertTimeToTimezone(
            slot.slot_end_time, 
            providerTimezone, 
            displayTimezone,
            slot.slot_date
          ),
          display_timezone: displayTimezone,
          // Add formatted display strings
          formatted_start_time: formatTimeInTimezone(
            convertTimeToTimezone(slot.slot_start_time, providerTimezone, displayTimezone, slot.slot_date),
            displayTimezone
          ),
          formatted_end_time: formatTimeInTimezone(
            convertTimeToTimezone(slot.slot_end_time, providerTimezone, displayTimezone, slot.slot_date),
            displayTimezone
          )
        }))
        
        return { data: convertedSlots, error: null }
      }
      
      // RPC failed (likely due to RLS), generate slots manually
      console.log('🔄 RLS BYPASS: Generating slots manually since RPC blocked by policies')
      
      // Get existing appointments to exclude booked times using service key to bypass RLS
      console.log(`🔍 APPOINTMENT LOOKUP: Checking for existing appointments for provider: ${providerId}`)
      console.log(`🔍 APPOINTMENT LOOKUP: Date range: ${startDate} to ${endDate}`)
      
      let appointments = []
      
      try {
        // First try regular client
        const { data: regularAppointments, error: appointmentError } = await supabase
          .from('appointments')
          .select('appointment_date, start_time, status, provider_id')
          .eq('provider_id', providerId)
          .in('status', ['scheduled', 'confirmed'])
          .gte('appointment_date', startDate)
          .lte('appointment_date', endDate)
        
        if (appointmentError) {
          console.log('⚠️ Regular appointment query failed, trying service key approach:', appointmentError)
          
          // Use service key approach as fallback
          const baseUrl = getEnvironmentAwareSupabaseUrl()
          const serviceKey = getEnvironmentAwareSupabaseServiceKey()
          
          const appointmentResponse = await fetch(
            `${baseUrl}/rest/v1/appointments?provider_id=eq.${providerId}&status=in.(scheduled,confirmed)&appointment_date=gte.${startDate}&appointment_date=lte.${endDate}&select=appointment_date,start_time,status,provider_id`,
            {
              headers: {
                'apikey': serviceKey,
                'Authorization': `Bearer ${serviceKey}`,
                'Content-Type': 'application/json'
              }
            }
          )
          
          if (appointmentResponse.ok) {
            appointments = await appointmentResponse.json()
            console.log('✅ Service key appointment lookup successful')
          } else {
            console.log('❌ Service key appointment lookup failed:', appointmentResponse.status, appointmentResponse.statusText)
          }
        } else {
          appointments = regularAppointments || []
          console.log('✅ Regular appointment lookup successful')
        }
      } catch (error) {
        console.log('❌ Appointment lookup exception:', error)
      }
      
      console.log('📅 Found existing appointments:', appointments?.length || 0)
      if (appointments && appointments.length > 0) {
        console.log('📅 Appointment details:', appointments)
      }
      
      // Create a set of booked time slots for quick lookup
      const bookedSlots = new Set()
      if (appointments) {
        appointments.forEach(apt => {
          const key = `${apt.appointment_date}_${apt.start_time}`
          bookedSlots.add(key)
          console.log(`🚫 Booked slot: ${apt.appointment_date} ${apt.start_time}`)
        })
      }
      
      // Generate available slots manually (Monday-Friday, 9 AM - 5 PM)
      console.log('⚠️ No slots from RPC, generating manual slots with appointment exclusions')
      console.log(`🚨 DEBUG: About to loop through dates from ${startDate} to ${endDate}`)
      
      const slots = []
      // FIX: Parse dates correctly to avoid timezone issues
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number)
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number)
      const start = new Date(startYear, startMonth - 1, startDay)  // Month is 0-indexed
      const end = new Date(endYear, endMonth - 1, endDay)
      console.log(`🚨 DEBUG: Date loop initialized - start: ${start.toISOString()}, end: ${end.toISOString()}`)
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        // Skip weekends for simplicity
        const dayOfWeek = d.getDay()
        const dateStr = d.toISOString().split('T')[0]
        console.error(`🚨 CRITICAL DEBUG: Checking ${dateStr} (dayOfWeek: ${dayOfWeek})`)
        console.warn(`🚨 CRITICAL DEBUG: Checking ${dateStr} (dayOfWeek: ${dayOfWeek})`)
        
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          console.error(`🚨 WEEKEND SKIP: ${dateStr} (dayOfWeek: ${dayOfWeek})`)
          continue
        }
        
        // Skip past dates - compare only date portion, not time
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Reset to start of day
        const currentDay = new Date(d)
        currentDay.setHours(0, 0, 0, 0) // Reset to start of day
        if (currentDay < today) continue
        
        // Check provider_schedules table for availability on this day
        let providerAvailable = false
        try {
          const { data: schedules, error: scheduleError } = await supabase
            .from('provider_schedules')
            .select('is_available')
            .eq('provider_id', providerId)
            .eq('day_of_week', dayOfWeek)
          
          if (scheduleError) {
            console.log(`⚠️ Error checking provider schedule for day ${dayOfWeek}:`, scheduleError.message)
            console.log(`🚫 DATABASE ERROR: Cannot verify provider availability, skipping day ${dateStr} for safety`)
            // If we can't check schedule, assume NOT available for safety
            providerAvailable = false
          } else if (schedules && schedules.length > 0) {
            // If any schedule entry for this day has is_available=true, the day is available
            providerAvailable = schedules.some(schedule => schedule.is_available === true)
            console.log(`📅 Provider availability check for ${dateStr} (day ${dayOfWeek}): ${providerAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'}`)
          } else {
            // No schedule entries for this day, assume not available
            providerAvailable = false
            console.log(`📅 No schedule entries found for ${dateStr} (day ${dayOfWeek}), assuming NOT AVAILABLE`)
          }
        } catch (error) {
          console.log(`❌ Exception checking provider schedule:`, error)
          console.log(`🚫 EXCEPTION: Cannot verify provider availability, skipping day ${dateStr} for safety`)
          // If we can't check schedule, assume NOT available for safety
          providerAvailable = false
        }
        
        // Skip this day if provider is not available
        if (!providerAvailable) {
          console.error(`🚨 PROVIDER NOT AVAILABLE SKIP: ${dateStr} - provider not available per schedule`)
          continue
        }
        
        console.error(`🚨 PROCEEDING TO GENERATE SLOTS: ${dateStr} day ${dayOfWeek}`)
        
        // Generate slots for full business hours (9 AM - 5 PM, excluding lunch 12-1 PM)
        for (let hour = 9; hour < 17; hour++) {
          // Skip lunch hour
          if (hour === 12) continue
          
          // Generate 30-minute slots
          for (let minutes of [0, 30]) {
            const startTime = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
            const endHour = minutes === 30 ? hour + 1 : hour
            const endMinutes = minutes === 30 ? 0 : 30
            const endTime = `${endHour.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}:00`
            
            // Check if this slot is booked
            const slotKey = `${dateStr}_${startTime}`
            if (bookedSlots.has(slotKey)) {
              console.log(`🚫 Excluding booked slot: ${dateStr} ${startTime}`)
              continue
            }
            
            // Add timezone-aware slot data
            const displayStartTime = convertTimeToTimezone(startTime, providerTimezone, displayTimezone, dateStr)
            const displayEndTime = convertTimeToTimezone(endTime, providerTimezone, displayTimezone, dateStr)
            const formattedStartTime = formatTimeInTimezone(displayStartTime, displayTimezone)
            const formattedEndTime = formatTimeInTimezone(displayEndTime, displayTimezone)
            
            console.log(`🕐 Slot ${startTime} ${providerTimezone} -> ${displayStartTime} ${displayTimezone} -> ${formattedStartTime}`)
            
            slots.push({
              slot_date: dateStr,
              slot_start_time: startTime,
              slot_end_time: endTime,
              duration_minutes: 30,
              // Timezone conversion fields
              provider_slot_time: startTime,
              provider_timezone: providerTimezone,
              display_start_time: displayStartTime,
              display_end_time: displayEndTime,
              display_timezone: displayTimezone,
              formatted_start_time: formattedStartTime,
              formatted_end_time: formattedEndTime
            })
          }
        }
      }
      
      console.log(`✅ Generated ${slots.length} manual slots with timezone conversion, excluding ${bookedSlots.size} booked slots`)
      console.log(`🌍 Slots converted from ${providerTimezone} to ${displayTimezone}`)
      return { data: slots.slice(0, 20), error: null } // Limit to 20 slots for testing
    } catch (error: any) {
      console.error('❌ Exception getting slots:', error)
      return { data: null, error }
    }
  }

  async rescheduleAppointment(
    appointmentId: string,
    appointmentDate: string,
    startTime: string,
    rescheduledBy: string,
    rescheduledByUserId: string,
    reason: string
  ) {
    try {
      console.log('🔄 Attempting to reschedule appointment:', {
        appointmentId,
        appointmentDate,
        startTime,
        rescheduledBy,
        rescheduledByUserId,
        reason
      })

      // Use direct update approach since RPC function has parameter issues
      console.log('⚠️ Using direct update approach for reschedule')
      
      // Calculate new end time
      const [hours, minutes] = startTime.split(':').map(Number)
      const startTimeObj = new Date()
      startTimeObj.setHours(hours, minutes, 0, 0)
      const endTimeObj = new Date(startTimeObj.getTime() + 30 * 60000) // Add 30 minutes
      const endTime = `${endTimeObj.getHours().toString().padStart(2, '0')}:${endTimeObj.getMinutes().toString().padStart(2, '0')}:00`
      
      // Update the appointment directly
      const updateResult = await supabase
        .from('appointments')
        .update({
          appointment_date: appointmentDate,
          start_time: startTime,
          end_time: endTime,
          admin_notes: `Rescheduled by ${rescheduledBy}${reason ? ': ' + reason : ''}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', appointmentId)
        .select()
        .single()
      
      const data = updateResult.data
      const error = updateResult.error

      if (error) {
        console.error('❌ Reschedule appointment error:', error)
        return { success: false, error }
      }

      console.log('✅ Reschedule appointment success:', data)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.error('❌ Reschedule appointment exception:', error)
      return { success: false, error }
    }
  }

  async cancelAppointment(
    appointmentId: string,
    cancelledBy: string,
    cancelledByUserId: string,
    reason: string
  ) {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          status: 'cancelled',
          cancelled_by: cancelledBy,
          cancelled_by_user_id: cancelledByUserId,
          cancellation_reason: reason,
          cancelled_at: new Date().toISOString()
        })
        .eq('id', appointmentId)
        .select()
        .single()

      if (error) {
        return { success: false, error }
      }

      return { success: true, data, error: null }
    } catch (error: any) {
      return { success: false, error }
    }
  }

  // Get all appointments for a specific provider
  async getProviderAppointments(providerProfileId: string) {
    try {
      console.log('🚀🚀🚀 GET PROVIDER APPOINTMENTS 2025-11-12-13:00')
      console.log('🔍 getProviderAppointments for provider profile ID:', providerProfileId)
      
      // Use environment-aware direct Supabase calls (same pattern as getPatientAppointments)
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      const serviceClient = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${serviceKey}`,
            'apikey': serviceKey,
            'Role': 'service_role'
          }
        }
      })

      // First get the provider table ID from the profile ID using service client
      const { data: providerData, error: providerError } = await serviceClient
        .from('providers')
        .select('id')
        .eq('profile_id', providerProfileId)
        .single()

      if (providerError || !providerData) {
        console.log('❌ Provider not found for profile ID:', providerProfileId, providerError)
        return { data: [], error: null }
      }

      console.log('🔍 Found provider table ID:', providerData.id)

      // Get appointments for this provider using service client to bypass RLS
      // Filter out appointments where Prescription filled = true
      const { data, error } = await serviceClient
        .from('appointments')
        .select(`
          *,
          patients:patient_id (
            id,
            profile_id
          )
        `)
        .eq('provider_id', providerData.id)
        .or('Prescription filled.is.null,Prescription filled.eq.false')
        .order('appointment_date', { ascending: false })

      if (error) {
        console.log('❌ Error fetching provider appointments:', error)
        return { data: null, error }
      }

      console.log('🔍 Raw provider appointment data:', data)

      // For each appointment, fetch patient profile info using the patient's profile_id
      const enrichedAppointments = await Promise.all(
        (data || []).map(async (appointment) => {
          try {
            let patientName = `Patient ${appointment.patient_id?.slice(0,8)}`
            
            // Get patient profile information if we have the profile_id
            if (appointment.patients?.profile_id) {
              const { data: profileData } = await serviceClient
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', appointment.patients.profile_id)
                .single()
              
              if (profileData?.first_name && profileData?.last_name) {
                patientName = `${profileData.first_name} ${profileData.last_name}`
              }
            }

            return {
              ...appointment,
              patient_name: patientName,
              // Keep original patient_id for compatibility with ProviderVisit interface
              patient_id: appointment.patients?.id || appointment.patient_id,
              // Include Async flag for UI badge display
              is_async: appointment.Async === true
            }
          } catch (err) {
            console.log('⚠️ Could not fetch patient info for appointment:', appointment.id, err)
            return {
              ...appointment,
              patient_name: `Patient ${appointment.patient_id?.slice(0,8)}`,
              patient_id: appointment.patients?.id || appointment.patient_id,
              is_async: appointment.Async === true
            }
          }
        })
      )

      console.log('✅ Provider appointment data with patient names:', enrichedAppointments)
      return { data: enrichedAppointments, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getProviderAppointments:', error)
      return { data: null, error }
    }
  }

  // Admin-specific methods
  async getAllPatients() {
    try {
      console.log('🔍 [FORCED DEPLOY] Getting all patients for admin dashboard - NEW CODE')
      
      // First get all patients (RLS disabled)
      const { data: patientsData, error: patientsError } = await supabase
        .from('patients')
        .select(`
          id,
          profile_id,
          date_of_birth,
          phone,
          has_completed_intake,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false })

      if (patientsError) {
        console.log('❌ Error fetching patients:', patientsError)
        return { data: null, error: patientsError }
      }

      console.log(`🔍 Found ${patientsData?.length || 0} patient records`)

      if (!patientsData || patientsData.length === 0) {
        console.log('✅ Found 0 patients')
        return { data: [], error: null }
      }

      // Get profile IDs to fetch separately
      const profileIds = patientsData.map(p => p.profile_id)
      
      // Fetch profiles using service role client to bypass RLS
      console.log('🔍 Using service role client to fetch patient profiles and bypass RLS')
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      // Build the profiles query URL with filters
      const profilesUrl = `${baseUrl}/rest/v1/profiles?id=in.(${profileIds.join(',')})&role=eq.patient&select=id,first_name,last_name,email,role`
      console.log('🔍 Service role profiles query URL:', profilesUrl)
      
      const profilesResponse = await fetch(profilesUrl, {
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json'
        }
      })
      
      let profilesData = null
      let profilesError = null
      
      if (profilesResponse.ok) {
        profilesData = await profilesResponse.json()
        console.log('✅ Service role profiles query successful')
      } else {
        profilesError = { message: `Service role profiles query failed: ${profilesResponse.status}` }
        console.log('❌ Service role profiles query failed:', profilesResponse.status)
      }

      if (profilesError) {
        console.log('❌ Error fetching profiles, using patients without profile data:', profilesError)
        // Return patients with minimal profile data
        const transformedData = patientsData.map(patient => ({
          id: patient.id,
          profile_id: patient.profile_id,
          first_name: 'Unknown',
          last_name: 'Patient',
          email: 'unknown@example.com',
          date_of_birth: patient.date_of_birth,
          phone: patient.phone,
          has_completed_intake: patient.has_completed_intake,
          assigned_providers: [],
          treatment_types: [],
          medications: [],
          created_at: patient.created_at,
          updated_at: patient.updated_at
        }))
        console.log(`✅ Found ${transformedData.length} patients (without profile data)`)
        return { data: transformedData, error: null }
      }

      console.log(`🔍 Found ${profilesData?.length || 0} patient profiles`)

      // Fetch provider assignments for each patient
      console.log('🔍 Fetching provider assignments for admin view')
      const patientIds = patientsData.map(p => p.id)
      
      let assignmentData = []
      if (patientIds.length > 0) {
        try {
          // First get assignments
          const { data: assignments, error: assignmentError } = await supabase
            .from('patient_assignments')
            .select('patient_id, provider_id, treatment_type, is_primary')
            .in('patient_id', patientIds)
          
          if (assignmentError) {
            console.log('❌ Error fetching assignments:', assignmentError)
            assignmentData = []
          } else if (assignments && assignments.length > 0) {
            console.log(`🔍 Found ${assignments.length} assignments, now getting provider details`)
            
            // Get unique provider IDs
            const providerIds = [...new Set(assignments.map(a => a.provider_id))]
            console.log(`🔍 Getting details for ${providerIds.length} unique providers`)
            
            // Get provider details with profile names
            const { data: providers, error: providerError } = await supabase
              .from('providers')
              .select(`
                id,
                specialty,
                profiles(first_name, last_name)
              `)
              .in('id', providerIds)
            
            if (providerError) {
              console.log('❌ Error fetching provider details:', providerError)
              // Create assignments with basic provider info
              assignmentData = assignments.map(a => ({
                ...a,
                provider_name: `Provider ${a.provider_id.slice(0, 8)}`
              }))
            } else {
              console.log(`✅ Found ${providers?.length || 0} provider details`)
              // Merge assignment and provider data
              assignmentData = assignments.map(assignment => {
                const provider = providers?.find(p => p.id === assignment.provider_id)
                const profile = provider?.profiles
                const firstName = profile?.first_name || 'Unknown'
                const lastName = profile?.last_name || 'Provider'
                const specialty = provider?.specialty || 'General'
                
                return {
                  ...assignment,
                  provider_name: `Dr. ${firstName} ${lastName} (${specialty})`
                }
              })
              console.log(`🔍 Created ${assignmentData.length} enriched assignments`)
            }
          } else {
            console.log('🔍 No provider assignments found')
          }
        } catch (assignmentError) {
          console.warn('⚠️ Exception fetching provider assignments:', assignmentError)
        }
      }

      // Fetch appointment data for all patients in bulk to optimize performance
      console.log('🔍 Fetching appointment data for all patients to enable visit columns')
      let appointmentsByPatient = new Map()
      
      try {
        // First check if we have any profile IDs to query
        if (profileIds.length === 0) {
          console.log('📝 No profile IDs to query appointments for')
        } else {
          console.log(`📝 Querying appointments for ${profileIds.length} profile IDs`)
          console.log('📝 Sample profile IDs:', profileIds.slice(0, 3))
        }
        
        // Try the appointments query with better error handling
        let appointmentsData = null
        let appointmentError = null
        
        if (profileIds.length > 0) {
          console.log('📝 Using get_patient_appointments function for each patient')
          
          // Use the get_patient_appointments function for each profile ID
          // This function handles the proper joins and RLS correctly
          const appointmentPromises = profileIds.map(async (profileId) => {
            try {
              const { data, error } = await supabase
                .rpc('get_patient_appointments', {
                  p_patient_profile_id: profileId,
                  p_include_past: true
                })

              if (error) {
                console.log(`⚠️ Error fetching appointments for profile ${profileId}:`, error)
                return { profileId, appointments: [] }
              }

              return { profileId, appointments: data || [] }
            } catch (err) {
              console.log(`⚠️ Exception fetching appointments for profile ${profileId}:`, err)
              return { profileId, appointments: [] }
            }
          })

          try {
            const appointmentResults = await Promise.all(appointmentPromises)
            
            // Flatten all appointments and add profile_id for mapping
            appointmentsData = []
            appointmentResults.forEach(({ profileId, appointments }) => {
              appointments.forEach(appt => {
                appointmentsData.push({
                  ...appt,
                  patient_profile_id: profileId, // Add this for mapping later
                  id: appt.appointment_id
                })
              })
            })
            
            console.log('✅ Successfully fetched appointments using get_patient_appointments function')
            console.log(`📝 Found ${appointmentsData.length} total appointments across all patients`)
          } catch (fetchError: any) {
            appointmentError = {
              message: 'Error fetching appointments via RPC function',
              details: fetchError.message
            }
            console.log('❌ Exception in appointment fetching:', fetchError)
          }
        }

        if (appointmentError) {
          console.log('⚠️ Warning: Error fetching appointment data:', appointmentError)
          console.log('⚠️ Error details:', appointmentError.message, appointmentError.details)
          // Continue without appointment data - patients will show "no visits"
        } else if (appointmentsData && appointmentsData.length > 0) {
          console.log(`✅ Found ${appointmentsData.length} appointments across all patients`)
          
          // Group appointments by patient profile_id and process for previous/next visits
          appointmentsData.forEach(appointment => {
            const patientId = appointment.patient_profile_id
            if (!appointmentsByPatient.has(patientId)) {
              appointmentsByPatient.set(patientId, [])
            }
            appointmentsByPatient.get(patientId).push({
              id: appointment.id,
              patient_profile_id: appointment.patient_profile_id,
              appointment_date: appointment.appointment_date,
              start_time: appointment.start_time,
              status: appointment.status,
              appointment_type: appointment.appointment_type
            })
          })
          
          // Process each patient's appointments to determine previous and next visits
          const now = new Date()
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          
          appointmentsByPatient.forEach((appointments, patientId) => {
            // Sort appointments chronologically
            appointments.sort((a, b) => new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime())
            
            // Find previous appointment (completed/confirmed appointments before today)
            let previousAppointment = null
            const pastAppointments = appointments.filter(appt => 
              new Date(appt.appointment_date) < today && 
              (appt.status === 'completed' || appt.status === 'confirmed')
            )
            if (pastAppointments.length > 0) {
              previousAppointment = pastAppointments[pastAppointments.length - 1]
            }
            
            // Find next appointment (future appointments from today onwards)
            let nextAppointment = null
            const futureAppointments = appointments.filter(appt => 
              new Date(appt.appointment_date) >= today && 
              (appt.status === 'confirmed' || appt.status === 'scheduled')
            )
            if (futureAppointments.length > 0) {
              nextAppointment = futureAppointments[0]
            }
            
            // Store processed appointment data
            appointmentsByPatient.set(patientId, {
              previous_appointment: previousAppointment,
              next_appointment: nextAppointment
            })
          })
          
          console.log(`✅ Processed appointment data for ${appointmentsByPatient.size} patients`)
        }
      } catch (appointmentError) {
        console.log('⚠️ Exception fetching appointment data:', appointmentError)
        // Continue without appointment data
      }

      // Join the data manually with appointment information
      const transformedData = patientsData
        .map(patient => {
          const profile = profilesData?.find(p => p.id === patient.profile_id)
          if (!profile) return null // Skip patients without patient role profiles
          
          // Get provider assignments for this patient
          const patientAssignments = assignmentData.filter(a => a.patient_id === patient.id)
          const assigned_providers = patientAssignments.map(a => a.provider_name || 'Unassigned')
          const treatment_types = [...new Set(patientAssignments.map(a => a.treatment_type).filter(Boolean))]
          
          // Get appointment data for this patient
          const appointmentInfo = appointmentsByPatient.get(patient.profile_id) || {
            previous_appointment: null,
            next_appointment: null
          }
          
          return {
            id: patient.id,
            profile_id: patient.profile_id,
            first_name: profile.first_name || 'Unknown',
            last_name: profile.last_name || 'Patient',
            email: profile.email || 'unknown@example.com',
            date_of_birth: patient.date_of_birth,
            phone: patient.phone,
            has_completed_intake: patient.has_completed_intake,
            assigned_providers: assigned_providers,
            treatment_types: treatment_types,
            medications: [], // TODO: Fetch medication data if needed for admin view
            created_at: patient.created_at,
            updated_at: patient.updated_at,
            // Add appointment data for visit columns
            previous_appointment: appointmentInfo.previous_appointment,
            next_appointment: appointmentInfo.next_appointment
          }
        })
        .filter(Boolean) // Remove null entries

      console.log(`✅ Found ${transformedData?.length || 0} patients`)
      console.log('✅ Transformed patient data:', transformedData)
      return { data: transformedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getAllPatients:', error)
      return { data: null, error }
    }
  }

  async getAllProviders() {
    try {
      console.log('🔍 Getting all providers for admin dashboard')
      
      const { data, error } = await supabase
        .from('providers')
        .select(`
          id,
          profile_id,
          specialty,
          license_number,
          phone,
          active,
          created_at,
          updated_at,
          profiles!inner (
            first_name,
            last_name,
            email,
            role
          )
        `)
        .order('created_at', { ascending: false })

      if (error) {
        console.log('❌ Error fetching all providers:', error)
        return { data: null, error }
      }

      console.log(`✅ Found ${data?.length || 0} providers`)
      
      // Transform the data to flatten profile information
      const transformedData = data?.map(provider => {
        const profile = provider.profiles as any;
        return {
          id: provider.id,
          profile_id: provider.profile_id,
          first_name: profile?.first_name || 'Unknown',
          last_name: profile?.last_name || 'User',
          email: profile?.email || '',
          specialty: provider.specialty,
          license_number: provider.license_number,
          phone: provider.phone,
          active: provider.active,
          created_at: provider.created_at,
          updated_at: provider.updated_at
        };
      }) || []

      console.log('✅ Transformed provider data:', transformedData)
      return { data: transformedData, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getAllProviders:', error)
      return { data: null, error }
    }
  }

  async updateAdminProfile(profileData: {
    first_name?: string
    last_name?: string
    email?: string
  }) {
    try {
      console.log('🔄 Updating admin profile:', profileData)
      
      const user = await this.getCurrentUser()
      if (!user.user) {
        return { success: false, error: new Error('No authenticated user') }
      }

      // Update the profiles table (admin profiles are stored in the general profiles table)
      const profileUpdate: any = {
        updated_at: new Date().toISOString()
      }

      if (profileData.first_name !== undefined) profileUpdate.first_name = profileData.first_name
      if (profileData.last_name !== undefined) profileUpdate.last_name = profileData.last_name
      if (profileData.email !== undefined) profileUpdate.email = profileData.email

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdate)
        .eq('id', user.user.id)

      if (profileError) {
        console.error('❌ Error updating admin profile:', profileError)
        return { success: false, error: profileError }
      }

      // Also update the admins table if it exists (for consistency)
      const adminUpdate: any = {
        updated_at: new Date().toISOString()
      }

      if (profileData.first_name !== undefined) adminUpdate.first_name = profileData.first_name
      if (profileData.last_name !== undefined) adminUpdate.last_name = profileData.last_name
      if (profileData.email !== undefined) adminUpdate.email = profileData.email

      const { error: adminError } = await supabase
        .from('admins')
        .update(adminUpdate)
        .eq('profile_id', user.user.id)

      // Don't fail if admin table update fails (it might not exist)
      if (adminError) {
        console.log('⚠️ Admin table update failed (might not exist):', adminError)
      }

      console.log('✅ Admin profile updated successfully')
      return { success: true, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating admin profile:', error)
      return { success: false, error }
    }
  }

  async createMedicationApprovalAndFax(preferenceId: string, providerProfileId: string, patientProfileId: string) {
    try {
      console.log('🚀🚀🚀 RLS BYPASS FIX createMedicationApprovalAndFax 2025-10-30-11:30')
      console.log('🔍 Creating medication approval and fax for preference:', preferenceId)
      console.log('🔍 FUNCTION PARAMETERS:', { preferenceId, providerProfileId, patientProfileId })
      
      // Use service client with RLS bypass for all database operations
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      const serviceClient = createClient(baseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${serviceKey}`,
            'apikey': serviceKey,
            'Role': 'service_role'
          }
        }
      })
      
      // Get provider table ID using service client
      const { data: providerData, error: providerError } = await serviceClient
        .from('providers')
        .select('id')
        .eq('profile_id', providerProfileId)
        .single()

      if (providerError || !providerData) {
        console.error('❌ Provider not found for profile ID:', providerProfileId, providerError)
        return { data: null, error: providerError }
      }

      const providerId = providerData.id
      console.log('✅ Found provider table ID:', providerId, 'for profile ID:', providerProfileId)

      // Get patient table ID using service client
      const { data: patientData, error: patientError } = await serviceClient
        .from('patients')
        .select('id')
        .eq('profile_id', patientProfileId)
        .single()

      if (patientError || !patientData) {
        console.error('❌ Patient not found for profile ID:', patientProfileId, patientError)
        return { data: null, error: patientError }
      }

      const patientId = patientData.id
      console.log('✅ Found patient table ID:', patientId, 'for profile ID:', patientProfileId)
      
      // First check if approval already exists using service client
      // Skip existing approval check for now to avoid RLS issues - just create new one
      const existingApproval = null

      let approvalId = existingApproval?.id

      if (!approvalId) {
        // Create new approval using service client
        const { data: newApproval, error: approvalError } = await serviceClient
          .from('medication_approvals')
          .insert({
            preference_id: preferenceId,
            provider_id: providerId, // Legacy column
            provider_profile_id: providerProfileId, // New simplified column
            status: 'approved',
            approval_date: new Date().toISOString()
          })
          .select('id')
          .single()

        if (approvalError) {
          console.error('❌ Error creating approval:', approvalError)
          return { data: null, error: approvalError }
        }

        approvalId = newApproval.id
        console.log('✅ Created new approval:', approvalId)
      } else {
        console.log('✅ Using existing approval:', approvalId)
      }

      // Create fax record using service client with ALL required fields for database trigger
      const { data: faxRecord, error: faxError } = await serviceClient
        .from('faxes')
        .insert({
          approval_id: approvalId,                    // ✅ Required by create_order_on_fax trigger
          medication_preference_id: preferenceId,    // ✅ Required by trigger
          patient_id: patientId,                     // ✅ Required by trigger (existing column)
          provider_id: providerId,                   // ✅ Required by trigger (existing column)
          fax_number: 'pharmacy-fax-number',
          prescription_details: 'Prescription faxed to pharmacy',
          status: 'sent',                            // ✅ Good practice
          sent_at: new Date().toISOString()          // ✅ Good practice
        })
        .select()
        .single()

      if (faxError) {
        console.error('❌ Error creating fax record:', faxError)
        return { data: null, error: faxError }
      }

      // Update preference with faxed timestamp using service client
      const { error: updateError } = await serviceClient
        .from('patient_medication_preferences')
        .update({ 
          faxed: new Date().toISOString(),
          status: 'approved'
        })
        .eq('id', preferenceId)

      if (updateError) {
        console.error('❌ Error updating preference:', updateError)
        return { data: null, error: updateError }
      }

      console.log('✅ Successfully created approval and fax workflow with simplified profile IDs')
      return { data: { approvalId, faxId: faxRecord.id }, error: null }

    } catch (error: any) {
      console.error('❌ Exception in createMedicationApprovalAndFax:', error)
      return { data: null, error }
    }
  }

  async getHealthMetrics(targetUserId: string, metricTypes: string[], startDate: string, endDate: string, limit: number = 1000) {
    try {
      console.log('🔍 getHealthMetrics for user profile ID:', targetUserId)
      console.log('🔍 Metric types:', metricTypes)
      console.log('🔍 Date range:', startDate, 'to', endDate)

      // First get the patient table ID from the profile ID
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: [], error: null }
      }

      console.log('🔍 Found patient table ID:', patientData.id)

      // Get health metrics for the patient
      console.log('🔍 Querying patient_health_metrics for patient_id:', patientData.id)
      
      // TEMPORARY: Use service role client to bypass RLS for health metrics
      // TODO: Update RLS policies to allow provider access to patient health metrics
      const { createClient } = await import('@supabase/supabase-js')
      const baseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      const serviceSupabase = createClient(baseUrl, serviceKey)
      
      console.log('🔍 Using service role client to bypass RLS for health metrics')
      const { data, error } = await serviceSupabase
        .from('patient_health_metrics')
        .select('*')
        .eq('patient_id', patientData.id)
        .in('metric_type', metricTypes)
        .gte('recorded_at', startDate)
        .lte('recorded_at', endDate)
        .order('recorded_at', { ascending: false })
        .limit(limit)
      
      console.log('🔍 Health metrics query completed. Data:', data?.length || 0, 'records. Error:', error)

      if (error) {
        console.log('❌ Error fetching health metrics:', error)
        return { data: null, error }
      }

      console.log('🔍 Raw health metrics data:', data)

      // Transform the data to match the expected format
      const transformedMetrics = (data || []).map(metric => ({
        patientId: metric.patient_id,
        metricType: metric.metric_type,
        value: metric.value,
        unit: metric.unit,
        recordedAt: metric.recorded_at,
        syncedFrom: metric.synced_from,
        metadata: metric.metadata
      }))

      console.log('🔍 Transformed health metrics:', transformedMetrics.length, 'records')
      
      return { data: transformedMetrics, error: null }
    } catch (error: any) {
      console.error('❌ Exception in getHealthMetrics:', error)
      return { data: null, error }
    }
  }

  // Profile update methods for Account Dialog
  async updateUserProfile(profileData: {
    firstName?: string
    lastName?: string
    email?: string
  }) {
    try {
      const { user } = await this.getCurrentUser()
      if (!user) return { success: false, error: new Error('No user found') }

      console.log('🔄 Updating user profile:', profileData)

      // Update profiles table
      const { data, error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          email: profileData.email,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single()

      if (error) {
        console.error('❌ Error updating profile:', error)
        return { success: false, error }
      }

      // If email was changed, update auth user email as well
      if (profileData.email && profileData.email !== user.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: profileData.email
        })

        if (authError) {
          console.error('❌ Error updating auth email:', authError)
          // Note: Profile was updated but auth email failed
          return { success: false, error: authError }
        }
      }

      console.log('✅ Profile updated successfully:', data)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating profile:', error)
      return { success: false, error }
    }
  }

  // Temporary method to manually set BMI for testing
  async setTestPatientBMI(bmi: number) {
    try {
      console.log('🧪 TEST: Setting patient BMI to', bmi)
      const result = await this.updatePatientProfile({ bmi })
      console.log('🧪 TEST: BMI update result:', result)
      return result
    } catch (error) {
      console.error('🧪 TEST: Error setting BMI:', error)
      return { success: false, error }
    }
  }

  // Helper function to set patient eligibility based on BMI
  async setPatientEligibility(profileId?: string, bmi?: number) {
    try {
      const targetUserId = profileId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { success: false, error: new Error('No user ID') }

      console.log('🔍 Setting patient eligibility for BMI:', bmi)
      
      // Calculate eligibility based on BMI
      let eligibility = []
      if (bmi && bmi >= 27) {
        eligibility = ['weight_loss']
        console.log('✅ Patient qualifies for weight_loss eligibility (BMI ≥ 27)')
      } else {
        console.log('⚠️ Patient does not qualify for weight_loss eligibility (BMI < 27)')
      }

      const { error: patientError } = await supabase
        .from('patients')
        .update({
          Eligibility: eligibility,
          bmi: bmi,
          updated_at: new Date().toISOString()
        })
        .eq('profile_id', targetUserId)

      if (patientError) {
        console.error('❌ Error updating patient eligibility:', patientError)
        return { success: false, error: patientError }
      }

      console.log('✅ Patient eligibility updated successfully:', eligibility)
      return { success: true, eligibility }
    } catch (error) {
      console.error('❌ Exception setting patient eligibility:', error)
      return { success: false, error }
    }
  }

  async updatePatientProfile(profileData: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    dateOfBirth?: string
    allergies?: string | string[]
    bmi?: number
    height_feet?: number
    height_inches?: number
    weight?: number
  }) {
    try {
      const { user } = await this.getCurrentUser()
      if (!user) return { success: false, error: new Error('No user found') }

      console.log('🔄 Updating patient profile:', profileData)

      // Update profiles table if any profile fields are provided
      if (profileData.firstName !== undefined || profileData.lastName !== undefined || profileData.email !== undefined) {
        const profileUpdate: any = {}
        if (profileData.firstName !== undefined) profileUpdate.first_name = profileData.firstName
        if (profileData.lastName !== undefined) profileUpdate.last_name = profileData.lastName
        if (profileData.email !== undefined) profileUpdate.email = profileData.email
        profileUpdate.updated_at = new Date().toISOString()

        const { error: profileError } = await supabase
          .from('profiles')
          .update(profileUpdate)
          .eq('id', user.id)

        if (profileError) {
          console.error('❌ Error updating profile:', profileError)
          return { success: false, error: profileError }
        }
      }

      // Update patients table if any patient fields are provided
      if (profileData.firstName !== undefined || profileData.lastName !== undefined || profileData.phone !== undefined || profileData.email !== undefined || profileData.dateOfBirth !== undefined || profileData.allergies !== undefined || profileData.bmi !== undefined || profileData.height_feet !== undefined || profileData.height_inches !== undefined || profileData.weight !== undefined) {
        const patientUpdate: any = {}
        if (profileData.firstName !== undefined) patientUpdate.first_name = profileData.firstName
        if (profileData.lastName !== undefined) patientUpdate.last_name = profileData.lastName
        if (profileData.phone !== undefined) patientUpdate.phone = profileData.phone
        if (profileData.email !== undefined) patientUpdate.email = profileData.email
        if (profileData.dateOfBirth !== undefined) patientUpdate.date_of_birth = profileData.dateOfBirth
        if (profileData.allergies !== undefined) {
          // Handle allergies as array (like clinical notes)
          patientUpdate.allergies = Array.isArray(profileData.allergies) 
            ? profileData.allergies 
            : (profileData.allergies ? [profileData.allergies] : [])
        }
        if (profileData.bmi !== undefined) patientUpdate.bmi = profileData.bmi
        if (profileData.height_feet !== undefined) patientUpdate.height_feet = profileData.height_feet
        if (profileData.height_inches !== undefined) patientUpdate.height_inches = profileData.height_inches
        if (profileData.weight !== undefined) patientUpdate.weight = profileData.weight
        patientUpdate.updated_at = new Date().toISOString()

        console.log('🔧 Patient update data:', patientUpdate)

        const { error: patientError } = await supabase
          .from('patients')
          .update(patientUpdate)
          .eq('profile_id', user.id)

        if (patientError) {
          console.error('❌ Error updating patient data:', patientError)
          return { success: false, error: patientError }
        }

        // If BMI was updated, recalculate eligibility
        if (profileData.bmi !== undefined) {
          console.log('🔍 BMI updated, recalculating eligibility...')
          await this.setPatientEligibility(user.id, profileData.bmi)
        }
      }

      // If email was changed, update auth user email as well
      if (profileData.email && profileData.email !== user.email) {
        console.log('🔄 Attempting to update auth email from', user.email, 'to', profileData.email)
        
        try {
          // Use admin client for immediate email updates in development
          const { createClient } = await import('@supabase/supabase-js')
          const baseUrl = getEnvironmentAwareSupabaseUrl()
          const serviceKey = getEnvironmentAwareSupabaseServiceKey()
          const adminSupabase = createClient(baseUrl, serviceKey)
          
          const { data: authData, error: authError } = await adminSupabase.auth.admin.updateUserById(
            user.id,
            { email: profileData.email }
          )

          if (authError) {
            console.error('❌ Error updating auth email via admin:', authError)
            console.log('⚠️ Profile updated but auth email update failed')
          } else {
            console.log('✅ Auth email updated successfully via admin:', authData.user.email)
          }
        } catch (adminError) {
          console.error('❌ Admin auth update failed, falling back to user update:', adminError)
          
          // Fallback to regular user update
          const { data: authData, error: authError } = await supabase.auth.updateUser({
            email: profileData.email
          })

          if (authError) {
            console.error('❌ User auth email update also failed:', authError)
            console.log('⚠️ Profile updated but auth email update failed')
          } else {
            console.log('✅ Auth email update initiated via user client:', authData)
          }
        }
      }

      // If firstName or lastName was changed, update auth user display_name as well
      if (profileData.firstName !== undefined || profileData.lastName !== undefined) {
        const firstName = profileData.firstName !== undefined ? profileData.firstName : (user.user_metadata?.first_name || '')
        const lastName = profileData.lastName !== undefined ? profileData.lastName : (user.user_metadata?.last_name || '')
        const displayName = `${firstName} ${lastName}`.trim()
        
        console.log('🔄 Updating auth display_name to:', displayName)
        
        try {
          // Use admin client for immediate display name updates
          const { createClient } = await import('@supabase/supabase-js')
          const baseUrl = getEnvironmentAwareSupabaseUrl()
          const serviceKey = getEnvironmentAwareSupabaseServiceKey()
          const adminSupabase = createClient(baseUrl, serviceKey)
          
          const { data: authData, error: authError } = await adminSupabase.auth.admin.updateUserById(
            user.id,
            { 
              user_metadata: { 
                ...user.user_metadata,
                first_name: firstName,
                last_name: lastName,
                full_name: displayName
              }
            }
          )

          if (authError) {
            console.error('❌ Error updating auth display_name via admin:', authError)
            console.log('⚠️ Profile updated but auth display_name update failed')
          } else {
            console.log('✅ Auth display_name updated successfully via admin:', authData.user.user_metadata?.full_name)
          }
        } catch (adminError) {
          console.error('❌ Admin auth display_name update failed, falling back to user update:', adminError)
          
          // Fallback to regular user update
          const { data: authData, error: authError } = await supabase.auth.updateUser({
            data: { 
              first_name: firstName,
              last_name: lastName,
              full_name: displayName
            }
          })

          if (authError) {
            console.error('❌ User auth display_name update also failed:', authError)
            console.log('⚠️ Profile updated but auth display_name update failed')
          } else {
            console.log('✅ Auth display_name update initiated via user client:', authData)
          }
        }
      }

      console.log('✅ Patient profile updated successfully')
      return { success: true, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating patient profile:', error)
      return { success: false, error }
    }
  }

  async updatePatientOnboardingStatus(patientProfileId: string, onboardingCompleted: boolean) {
    try {
      console.log('🔄 Updating patient onboarding status:', { patientProfileId, onboardingCompleted })

      const { error } = await supabase
        .from('patients')
        .update({ 
          onboarding_completed: onboardingCompleted,
          updated_at: new Date().toISOString()
        })
        .eq('profile_id', patientProfileId)

      if (error) {
        console.error('❌ Error updating onboarding status:', error)
        return { success: false, error }
      }

      console.log('✅ Patient onboarding status updated successfully')
      return { success: true, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating onboarding status:', error)
      return { success: false, error }
    }
  }

  async updatePassword(currentPassword: string, newPassword: string) {
    try {
      console.log('🔄 Updating user password')

      // Supabase requires current session to update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) {
        console.error('❌ Error updating password:', error)
        return { success: false, error }
      }

      console.log('✅ Password updated successfully')
      return { success: true, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating password:', error)
      return { success: false, error }
    }
  }

  // Patient address methods for shipping addresses
  async getPatientAddresses(userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { data: null, error: new Error('No user ID') }

      console.log('🔍 getPatientAddresses for user profile ID:', targetUserId)

      // First get the patient table ID from the profile ID
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        console.log('❌ Patient not found for profile ID:', targetUserId, patientError)
        return { data: [], error: null }
      }

      console.log('🔍 Found patient table ID:', patientData.id)

      // Get patient addresses
      const { data, error } = await supabase
        .from('patient_addresses')
        .select('*')
        .eq('patient_id', patientData.id)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) {
        console.log('❌ Error fetching patient addresses:', error)
        return { data: null, error }
      }

      console.log('🔍 Found patient addresses:', data)
      return { data, error: null }
    } catch (error: any) {
      console.log('❌ Exception in getPatientAddresses:', error)
      return { data: null, error }
    }
  }

  async createPatientAddress(addressData: {
    streetLine1: string
    streetLine2?: string
    city: string
    state: string
    zipCode: string
    addressType?: string
    isPrimary?: boolean
  }, userId?: string) {
    try {
      const targetUserId = userId || (await this.getCurrentUser()).user?.id
      if (!targetUserId) return { success: false, error: new Error('No user ID') }

      console.log('🔄 Creating patient address:', addressData)

      // First get the patient table ID from the profile ID
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('profile_id', targetUserId)
        .single()

      if (patientError || !patientData) {
        return { success: false, error: new Error('Patient record not found') }
      }

      // If setting as primary, unset other primary addresses
      if (addressData.isPrimary) {
        await supabase
          .from('patient_addresses')
          .update({ is_primary: false })
          .eq('patient_id', patientData.id)
          .eq('is_primary', true)
      }

      const { data, error } = await supabase
        .from('patient_addresses')
        .insert({
          patient_id: patientData.id,
          street_line_1: addressData.streetLine1,
          street_line_2: addressData.streetLine2,
          city: addressData.city,
          state: addressData.state,
          zip_code: addressData.zipCode,
          address_type: addressData.addressType || 'shipping',
          is_primary: addressData.isPrimary || false
        })
        .select()
        .single()

      if (error) {
        console.error('❌ Error creating patient address:', error)
        return { success: false, error }
      }

      console.log('✅ Patient address created successfully:', data)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.error('❌ Exception creating patient address:', error)
      return { success: false, error }
    }
  }

  async updatePatientAddress(addressId: string, addressData: {
    streetLine1?: string
    streetLine2?: string
    city?: string
    state?: string
    zipCode?: string
    addressType?: string
    isPrimary?: boolean
  }) {
    try {
      console.log('🔄 Updating patient address:', addressId, addressData)

      // If setting as primary, first unset other primary addresses for this patient
      if (addressData.isPrimary) {
        // Get the patient_id for this address first
        const { data: addressInfo } = await supabase
          .from('patient_addresses')
          .select('patient_id')
          .eq('id', addressId)
          .single()

        if (addressInfo) {
          await supabase
            .from('patient_addresses')
            .update({ is_primary: false })
            .eq('patient_id', addressInfo.patient_id)
            .eq('is_primary', true)
        }
      }

      const updateData: any = {}
      if (addressData.streetLine1 !== undefined) updateData.street_line_1 = addressData.streetLine1
      if (addressData.streetLine2 !== undefined) updateData.street_line_2 = addressData.streetLine2
      if (addressData.city !== undefined) updateData.city = addressData.city
      if (addressData.state !== undefined) updateData.state = addressData.state
      if (addressData.zipCode !== undefined) updateData.zip_code = addressData.zipCode
      if (addressData.addressType !== undefined) updateData.address_type = addressData.addressType
      if (addressData.isPrimary !== undefined) updateData.is_primary = addressData.isPrimary
      updateData.updated_at = new Date().toISOString()

      const { data, error } = await supabase
        .from('patient_addresses')
        .update(updateData)
        .eq('id', addressId)
        .select()
        .single()

      if (error) {
        console.error('❌ Error updating patient address:', error)
        return { success: false, error }
      }

      console.log('✅ Patient address updated successfully:', data)
      return { success: true, data, error: null }
    } catch (error: any) {
      console.error('❌ Exception updating patient address:', error)
      return { success: false, error }
    }
  }

  async deletePatientAddress(addressId: string) {
    try {
      console.log('🔄 Deleting patient address:', addressId)

      const { error } = await supabase
        .from('patient_addresses')
        .delete()
        .eq('id', addressId)

      if (error) {
        console.error('❌ Error deleting patient address:', error)
        return { success: false, error }
      }

      console.log('✅ Patient address deleted successfully')
      return { success: true, error: null }
    } catch (error: any) {
      console.error('❌ Exception deleting patient address:', error)
      return { success: false, error }
    }
  }

  async approveMedicationPreference(medicationPreferenceId: string) {
    console.log('🔧 approveMedicationPreference called for ID:', medicationPreferenceId)
    
    try {
      // Use environment-aware service client for admin operations
      const { createClient } = await import('@supabase/supabase-js')
      const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js')
      
      const supabaseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      console.log('🔧 Using Supabase URL:', supabaseUrl)
      console.log('🔧 Service key length:', serviceKey?.length || 0)
      
      const adminClient = createClient(supabaseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        }
      })

      console.log('🔧 Approving medication preference:', medicationPreferenceId)
      
      const { data, error } = await adminClient
        .from('patient_medication_preferences')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', medicationPreferenceId)
        .select('*')

      if (error) {
        console.error('❌ Error approving medication preference:', error)
        return { success: false, error }
      }

      console.log('✅ Medication preference approved successfully:', data)
      return { success: true, data, error: null }
      
    } catch (error) {
      console.error('❌ Exception in approveMedicationPreference:', error)
      return { success: false, error }
    }
  }

  async createMedicationOrderForPreference(medicationPreferenceId: string, fulfillmentStatus: string = 'paid') {
    console.log('🔧 createMedicationOrderForPreference called for preference ID:', medicationPreferenceId)
    
    try {
      // Use environment-aware service client for admin operations
      const { createClient } = await import('@supabase/supabase-js')
      const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js')
      
      const supabaseUrl = getEnvironmentAwareSupabaseUrl()
      const serviceKey = getEnvironmentAwareSupabaseServiceKey()
      
      console.log('🔧 Using Supabase URL:', supabaseUrl)
      console.log('🔧 Service key length:', serviceKey?.length || 0)
      
      const adminClient = createClient(supabaseUrl, serviceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        }
      })

      // First get the medication preference to extract patient_id
      const { data: medPref, error: prefError } = await adminClient
        .from('patient_medication_preferences')
        .select('patient_id, medication_id, medications(name)')
        .eq('id', medicationPreferenceId)
        .single()

      if (prefError) {
        console.error('❌ Error fetching medication preference:', prefError)
        return { success: false, error: prefError }
      }

      console.log('🔧 Creating order for patient:', medPref.patient_id, 'medication:', medPref.medication_id)
      
      const orderData = {
        medication_preference_id: medicationPreferenceId,
        patient_id: medPref.patient_id,
        fulfillment_status: fulfillmentStatus,
        order_date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await adminClient
        .from('medication_orders')
        .insert(orderData)
        .select('*')

      if (error) {
        console.error('❌ Error creating medication order:', error)
        return { success: false, error }
      }

      console.log('✅ Medication order created successfully:', data)
      return { success: true, data, error: null }
      
    } catch (error) {
      console.error('❌ Exception in createMedicationOrderForPreference:', error)
      return { success: false, error }
    }
  }
}

export const authService = new AuthService()
export default authService